export type ImageStyle = "cover" | "contain" | "portrait";

export interface NewsArticle {
  id: string;
  title: string;
  titleEn?: string;
  excerpt: string;
  excerptEn?: string;
  categories: string[];
  date: string;
  image?: string;
  imageStyle?: ImageStyle;
  imageAlt?: string;
  imageAltEn?: string;
  featured?: boolean;
  content?: string;
  contentEn?: string;
  vimeoId?: string;
  images?: Record<string, string>;
  imageAlts?: Record<string, string>;
  imageAltsEn?: Record<string, string>;
  metaDescription?: string;   // ≤155 tecken; saknas → excerpt klipps (clampDescription)
  metaDescriptionEn?: string; // engelsk; saknas → excerptEn/metaDescription
  heading?: string;   // kort H1 (1–5 ord) på artikelsidan; saknas → title. <title>/korten använder alltid title
  headingEn?: string; // engelsk kort H1; saknas → titleEn
}

export const newsCategories = [
  "TGA",
  "TLE",
  "IFRS 16",
  "IFRS 18",
  "Tiego",
] as const;

const monthMap: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, maj: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, okt: 9, nov: 10, dec: 11,
};

function parseSwedishDate(d: string): Date {
  const [day, mon, year] = d.split(" ");
  return new Date(Number(year), monthMap[mon] ?? 0, Number(day));
}

// Articles – order doesn't matter, they are sorted automatically below.
const unsortedArticles: NewsArticle[] = [
  {
    // OBS: artikeln har en egen JSX-kropp (IfrsLunchBody) registrerad i
    // src/components/news/customArticleBodies.tsx. content/contentEn nedan är en
    // DSL-fallback som renderas där registret saknas (Lovable-ägda main).
    id: "35",
    title: "Du läser IASB-nyheterna. Men vem diskuterar du dem med?",
    titleEn: "You read the IASB news. But who do you discuss it with?",
    excerpt:
      "Tiego planerar ett mindre nätverk med återkommande IFRS-luncher för dig som arbetar kvalificerat med IFRS och koncernrapportering. Första tillfället: torsdag 1 oktober 2026 på Sveavägen 68 i Stockholm.",
    excerptEn:
      "Tiego is planning a small network of recurring IFRS lunches for people who work at a qualified level with IFRS and group reporting. First session: Thursday 1 October 2026 at Sveavägen 68 in Stockholm.",
    categories: ["Tiego", "IFRS 18"],
    date: "14 sep 2026",
    metaDescription: "Tiego startar ett nätverk med återkommande IFRS-luncher för dig som arbetar med IFRS och koncernrapportering. Första tillfället 1 oktober 2026 i Stockholm.",
    metaDescriptionEn: "Tiego is starting a network of recurring IFRS lunches for people working with IFRS and group reporting. First session 1 October 2026 in Stockholm.",
    image: "news/ifrs-group.webp",
    imageStyle: "cover",
    imageAlt: "Mattias Elfgren i samtal med kollegor på Tiego kring en laptop",
    imageAltEn: "Mattias Elfgren talking with colleagues at Tiego around a laptop",
    content: `Tiego planerar ett mindre nätverk med återkommande IFRS-luncher – för dig som vill gå från nyhet och normgivning till faktisk påverkan på redovisning och rapportering.

**Första tillfället**

• Datum: Torsdag 1 oktober 2026

• Tid: 11.30–13.30

• Plats: Tiego, Sveavägen 68, Stockholm

• Gäst: Fredrik Walmeus, Deloitte

Lunch ingår. Antalet platser är begränsat. Anmäl dig till filippa.hallberg@tiego.se och berätta gärna vilken IFRS-fråga du vill diskutera.

**För dig som följer utvecklingen**

Målgruppen är personer som arbetar kvalificerat med IFRS och koncernrapportering, men som inte alltid har en egen IFRS-avdelning att löpande pröva tankar och tolkningar med.

**Från omvärldsbevakning till praktisk tillämpning**

Varje lunch tar sin utgångspunkt i ett koncentrerat urval från IASB, IFRIC och EFRAG. En kort inledning följs av ett öppet samtal mellan personer med erfarenhet av redovisning, rapportering och genomförande.

• Bevaka: Vad är nytt – och vad är på väg?

• Förstå: Vilka bedömningar och processer påverkas?

• Genomföra: Vad krävs i data, system och rapporter?

Ett forum för erfarenhetsutbyte – inte en föreläsningsserie och inte ett säljmöte.

**Ett mindre forum med hög relevans – vad skulle vi kunna diskutera?**

Temana väljs nära varje träff och sätts alltid i relation till företagens verkliga rapportering. Följande frågor är aktuella exempel inför hösten 2026.

• IFRS 18 – Från standard till genomförande: Resultatkategorier, nya delsummor, MPM, aggregering och jämförelsetal. Vilka förändringar krävs i kontoplan, rapportpaket och koncernrapportering inför 2027?

• IAS 7 – Kassaflödet under utveckling: IASB arbetar vidare med kassaflödesanalysen och relaterade frågor. Hur skapar vi ett robust flöde från IB–UB-analys till rapport och noter?

• IFRS 16 – Vad återstår efter införandet? Post-implementation review aktualiserar bedömningar, datakvalitet och praktiska tillämpningsfrågor inom leasingredovisning.

• IAS 38 – Immateriella tillgångar i en digital ekonomi: IASB prövar hur dagens krav fungerar för moderna affärsmodeller, bland annat med SaaS som testfall.

• IFRS 3 – Förvärv, goodwill och uppföljning: Kommande upplysningar kan påverka vilken information om förväntade synergier och förvärvsresultat som behöver fångas och följas över tid.

**Så kan en IFRS-lunch se ut**

• 20 minuter: Koncentrerad uppdatering från IASB, IFRIC och EFRAG

• 20 minuter: Konsekvenser för redovisning, data, rapportpaket och systemstöd

• 30 minuter: Rundabordssamtal med erfarenhetsutbyte och gemensamma frågor

• Efteråt: En kort sammanfattning och förslag på nästa tema

**Vi börjar i liten skala**

Tiego bjuder först in ett mindre antal kunder tillsammans med någon redovisningsexpert. Ambitionen är att skapa ett förtroendefullt samtal där deltagarna både får och bidrar med kunskap. Därefter låter vi gruppens behov forma fortsättningen.

**Är du intresserad av att delta?**

Anmäl ditt intresse och berätta gärna vilken IFRS-fråga du vill diskutera. Första tillfället är den 1 oktober på Sveavägen 68 – och du är lika välkommen att anmäla intresse för kommande luncher.

Anmälan: filippa.hallberg@tiego.se (ämne "Anmälan IFRS-lunch"). Frågor om innehållet: Mattias Elfgren, VD Tiego, mattias.elfgren@tiego.se.

Källor och lästips: IFRS Foundation, IFRS 18 Presentation and Disclosure in Financial Statements; IASB meeting papers, 21–22 July 2026 (IFRS 16, IAS 38, business combinations, cash flows and related matters). Se ifrs.org.`,
    contentEn: `Tiego is planning a small network of recurring IFRS lunches – for those who want to move from news and standard-setting to real impact on accounting and reporting.

**First session**

• Date: Thursday 1 October 2026

• Time: 11:30–13:30

• Venue: Tiego, Sveavägen 68, Stockholm

• Guest: Fredrik Walmeus, Deloitte

Lunch is included. Places are limited. Register with filippa.hallberg@tiego.se and feel free to tell us which IFRS issue you would like to discuss.

**For those who follow the developments**

The target group is people who work at a qualified level with IFRS and group reporting, but who do not always have an in-house IFRS department to test ideas and interpretations with on an ongoing basis.

**From monitoring to practical application**

Each lunch starts from a concentrated selection of developments at the IASB, IFRIC and EFRAG. A short introduction is followed by an open conversation between people with experience in accounting, reporting and implementation.

• Monitor: What is new – and what is on the way?

• Understand: Which judgements and processes are affected?

• Implement: What is required in terms of data, systems and reports?

A forum for exchanging experience – not a lecture series and not a sales meeting.

**A small forum with high relevance – what could we discuss?**

Themes are chosen close to each session and are always related to companies' actual reporting. The following questions are current examples ahead of autumn 2026.

• IFRS 18 – From standard to implementation: Income statement categories, new subtotals, MPM, aggregation and comparatives. What changes are needed in the chart of accounts, reporting packages and group reporting ahead of 2027?

• IAS 7 – The cash flow statement under development: The IASB continues its work on the statement of cash flows and related matters. How do we build a robust flow from opening-to-closing balance analysis to the statement and the notes?

• IFRS 16 – What remains after implementation? The post-implementation review brings judgements, data quality and practical application issues in lease accounting back onto the agenda.

• IAS 38 – Intangible assets in a digital economy: The IASB is examining how today's requirements work for modern business models, using SaaS as one test case.

• IFRS 3 – Acquisitions, goodwill and follow-up: Forthcoming disclosures may affect what information about expected synergies and acquisition performance needs to be captured and tracked over time.

**What an IFRS lunch could look like**

• 20 minutes: Concentrated update from the IASB, IFRIC and EFRAG

• 20 minutes: Consequences for accounting, data, reporting packages and system support

• 30 minutes: Round-table discussion with exchange of experience and shared questions

• Afterwards: A short summary and a proposal for the next theme

**We start small**

Tiego will first invite a small number of clients together with an accounting expert. The ambition is to create a trusted conversation in which participants both gain and contribute knowledge. After that, we let the group's needs shape what comes next.

**Interested in taking part?**

Register your interest and feel free to tell us which IFRS issue you would like to discuss. The first session is on 1 October at Sveavägen 68 – and you are equally welcome to register interest in future lunches.

Registration: filippa.hallberg@tiego.se (subject "Registration IFRS lunch"). Questions about the content: Mattias Elfgren, CEO Tiego, mattias.elfgren@tiego.se.

Sources and further reading: IFRS Foundation, IFRS 18 Presentation and Disclosure in Financial Statements; IASB meeting papers, 21–22 July 2026 (IFRS 16, IAS 38, business combinations, cash flows and related matters). See ifrs.org.`,
  },
  {
    id: "34",
    title:
      "Junior konsult till Tiego – utmanas och väx med oss inom koncernrapportering",
    titleEn:
      "Junior Consultant at Tiego – Be Challenged and Grow With Us in Group Reporting",
    excerpt:
      "Uppdatering: Rekryteringen är avslutad – stort tack till alla som sökt! Vi sökte en junior konsult på heltid till vårt nyöppnade kontor i Norrköping med fokus på koncernredovisning, leasingredovisning och bokslutsprocesser.",
    excerptEn:
      "Update: The recruitment has been concluded – a big thank you to everyone who applied! We were looking for a full-time junior consultant for our newly opened office in Norrköping, focusing on group accounting, lease accounting and financial closing processes.",
    categories: ["Tiego"],
    date: "25 aug 2026",
    heading: "Junior konsult till Tiego",
    headingEn: "Junior Consultant at Tiego",
    metaDescription: "Rekryteringen är avslutad. Tiego sökte en junior konsult till kontoret i Norrköping med fokus på koncernredovisning, leasingredovisning och bokslut.",
    metaDescriptionEn: "The recruitment is closed. Tiego was looking for a junior consultant for our Norrköping office, focusing on group accounting, lease accounting and closing.",
    image: "news/noah-rasmus.webp",
    imageStyle: "cover",
    imageAlt: "Noah Nihlén och Rasmus Björn, koncernredovisningskonsulter på Tiego, arbetar tillsammans framför en skärm på kontoret",
    imageAltEn: "Noah Nihlén and Rasmus Björn, group accounting consultants at Tiego, working together at a screen in the office",
    content: `**Uppdatering 14 september 2026:** Ansökningstiden har gått ut och rekryteringen är avslutad. Vi vill rikta ett stort tack till alla som har sökt och visat intresse för Tiego.

Vill du utvecklas som ekonom inom koncernredovisning, leasingredovisning och bokslutsprocesser? Är du nyfiken på kundnära rådgivning och att arbeta med några av Nordens största företag? Då är det här rollen för dig.

Tiego växer och söker nu en junior konsult på heltid till vårt team i Norrköping.

Hos oss får du möjlighet att lära dig koncernredovisning, systemimplementation och kundnära problemlösning i praktiken, tillsammans med ett gäng sköna kollegor.

**Om Tiego**

Tiego utvecklar affärssystem för bland annat koncernredovisning och leasinghantering. Våra produkter används av börsnoterade bolag, fastighetskoncerner, industriföretag och offentliga verksamheter. Det gemensamma behovet är lösningar för affärskritiska processer där kvalitet, spårbarhet och tillförlitlighet är avgörande.

Hos oss kombineras redovisningskompetens, systemförståelse och kundnära problemlösning. Våra konsulter och utvecklare samarbetar i team för att löpande vidareutveckla våra produkter och möta kundernas behov.

Vi är idag 18 personer, med ett utvecklingsteam på sju personer och ett något större konsultteam. Vi har vårt huvudkontor i Stockholm men finns även i Luleå och Norrköping.

Vi har över tid byggt ett starkt team med bakgrund och erfarenhet från näringslivet, revisionsbyråer och relevanta utbildningar.

**Om rollen**

Som junior konsult hos Tiego får du en bred och utvecklande roll där du successivt bygger upp din kompetens inom koncern- och leasingredovisning.

Du kommer att arbeta tillsammans med mer erfarna kollegor i kundprojekt, supportärenden och interna förbättringsprojekt. Rollen innebär både praktiskt systemarbete, kontakt med kunder och arbete med redovisningsnära frågor.

Exempel på arbetsuppgifter:

• Stötta kunder vid implementation av Tiegos konsolideringssystem

• Hjälpa kunder i supportärenden kopplade till systemet och deras rapporteringsprocesser

• Arbeta med kunders boksluts- och koncernrapporteringsflöden

• Delta i genomgångar, utbildningar och workshops med kunder

• Felsöka, analysera och dokumentera system- och redovisningsfrågor

• Bidra till interna processförbättringar och dokumentation

• På sikt fördjupa dig i mer komplexa frågor inom exempelvis förvärv, avyttringar, fusioner, leasing, kassaflödesanalys och koncernredovisning

AI är en naturlig del av vårt dagliga utvecklings- och kundarbete. Vi använder AI-stöd för att effektivisera utveckling, kvalitetssäkring och problemlösning, samtidigt som vi utforskar hur AI kan skapa värde för våra kunder genom nya funktioner i produkterna.

Du förväntas inte kunna allt från början. Det är viktigare att du har ett intresse för redovisning, system och problemlösning samt en vilja att utvecklas inom området. Du behöver inte heller vara en avancerad AI-användare, men vi vill att du är nyfiken på hur tekniken kan användas i praktiken.

**Vad har du på CV:t?**

Vi söker dig som är i början av din karriär och vill utvecklas i en heltidsroll där koncernredovisning, system och kundkontakt möts. Din nyfikenhet, analytiska förmåga och vilja att lära dig är viktigare än hur många år du har arbetat.

Du kan vara nyutexaminerad eller ha arbetat något eller ett par år inom ekonomi och redovisning. Vi ser gärna att du har fått någon form av praktisk erfarenhet av redovisning, exempelvis genom arbete, praktik eller ett kvalificerat extrajobb. Din utbildning kan vara inriktad mot exempelvis redovisning, revision, controlling eller finansiell rapportering. Det viktigaste är att du tycker om att förstå hur saker hänger ihop, trivs med att samarbeta och vill utvecklas tillsammans med kunder och kollegor.

Vi tror att du har:

• Intresse för koncern- och leasingredovisning samt tillhörande regelverk

• Intresse för system, processer, digitalisering och AI

• God analytisk förmåga och en vilja att förstå hur saker hänger ihop

• Lätt för att kommunicera och samarbeta med andra

• Goda kunskaper i svenska och engelska

**Vem är du som person?**

Du är en nyfiken, noggrann och lösningsorienterad lagspelare. Du tycker om att lära dig nya saker och har förmåga att ta dig an både detaljer och helhetsperspektiv.

Eftersom rollen innebär kontakt med kunder är det viktigt att du är serviceinriktad, pedagogisk och trygg i dialog med andra. Du behöver också trivas med att arbeta strukturerat, ta ansvar och våga ställa frågor när något behöver förstås på djupet.

Vi tror att du passar hos oss om du vill arbeta i ett mindre specialistbolag där du får vara nära både kunder, kollegor och systemutveckling.

**Varför Tiego?**

På Tiego har vi målsättningen att bygga det företag och den arbetsplats där vi själva vill stanna länge. Det handlar delvis om utvecklingsmöjligheter genom kunskapsutbyte, modern teknik, smarta lösningar och utmanande kundprojekt. Till lika stor del handlar det om en arbetsmiljö där vi ser och respekterar varandra, hjälps åt och har roligt ihop. Det har fungerat bra så långt!

Vi står nu inför en spännande framtid där vi håller på att förbättra våra tidigare lösningar i Excel till moderna webbapplikationer. Där ger AI oss nya möjligheter att effektivisera och utveckla med utgångspunkt i vårt specialistkunnande. En tillväxtresa vi vill göra tillsammans med fler drivna kollegor.

**Omfattning och placering**

Tjänsten är en heltidsanställning med huvudplacering på vårt nyöppnade kontor i Norrköping. Kontoret leds av Rasmus Björn och inledningsvis kommer teamet på plats att bestå av Rasmus och du som blir vår nya kollega. Det ger dig möjlighet att vara med från början och bidra till hur vi utvecklar både arbetssättet och Tiegos närvaro i Norrköping.

Samtidigt blir du en del av hela Tiegos team och får ett nära samarbete med våra kollegor i Stockholm. Du kommer därför att arbeta från Stockholmskontoret i genomsnitt en dag per vecka, bland annat för kunskapsutbyte, gemensamt arbete i kundprojekt och andra aktiviteter tillsammans med teamet. Behovet kan variera något över tid. Tiego står för kostnaderna för resorna.

**Intresserad?**

Låter det här som jobbet för dig? Skicka in din ansökan med CV och personligt brev.

Vad lockar dig mest med en roll där redovisning, system och kundkontakt möts? Det vill vi gärna att du berättar om i ditt personliga brev.

Din ansökan tas emot av vår HR-ansvariga Katarina von Wachenfeldt via e-post på katarina.wachenfeldt@tiego.se. Katarina går igenom ansökningarna och genomför intervjuer löpande tillsammans med Rasmus Björn, ansvarig för vårt kontor i Norrköping.

Vi arbetar med egna resurser i denna rekrytering och undanber oss all kontakt med bemannings- och rekryteringsföretag samt försäljare av ytterligare jobbannonser eller rekryteringstjänster.`,
    contentEn: `**Update 14 September 2026:** Applications are now closed and the recruitment has been concluded. We would like to extend a big thank you to everyone who applied and showed interest in Tiego.

Do you want to develop as a finance professional within group accounting, lease accounting and financial closing processes? Are you curious about client-facing advisory work and working with some of the largest companies in the Nordics? Then this is the role for you.

Tiego is growing and we are now looking for a full-time junior consultant for our team in Norrköping.

With us, you get the opportunity to learn group accounting, system implementation and client-facing problem solving in practice, together with a great group of colleagues.

**About Tiego**

Tiego develops business systems for, among other things, group accounting and lease management. Our products are used by listed companies, real estate groups, industrial companies and public sector organisations. The common need is solutions for business-critical processes where quality, traceability and reliability are essential.

With us, accounting expertise, system understanding and client-facing problem solving come together. Our consultants and developers collaborate in teams to continuously develop our products and meet our customers' needs.

Today we are 18 people, with a development team of seven and a somewhat larger consulting team. Our head office is in Stockholm, but we are also present in Luleå and Norrköping.

Over time, we have built a strong team with backgrounds and experience from industry, audit firms and relevant academic programmes.

**About the role**

As a junior consultant at Tiego, you get a broad and developing role where you gradually build up your expertise within group and lease accounting.

You will work together with more experienced colleagues in customer projects, support cases and internal improvement projects. The role involves practical system work, customer contact and accounting-related questions.

Examples of tasks:

• Support customers during the implementation of Tiego's consolidation system

• Help customers with support cases related to the system and their reporting processes

• Work with customers' financial closing and group reporting flows

• Take part in reviews, trainings and workshops with customers

• Troubleshoot, analyse and document system and accounting questions

• Contribute to internal process improvements and documentation

• Over time, deepen your knowledge of more complex areas such as acquisitions, divestments, mergers, leasing, cash flow analysis and group accounting

AI is a natural part of our daily development and customer work. We use AI support to make development, quality assurance and problem solving more efficient, while exploring how AI can create value for our customers through new features in the products.

You are not expected to know everything from the start. It is more important that you have an interest in accounting, systems and problem solving, and a desire to develop within the field. Nor do you need to be an advanced AI user, but we want you to be curious about how the technology can be used in practice.

**What is on your CV?**

We are looking for someone at the beginning of their career who wants to develop in a full-time role where group accounting, systems and customer contact meet. Your curiosity, analytical ability and willingness to learn matter more than how many years you have worked.

You may be a recent graduate or have worked for a year or two in finance and accounting. We would like you to have gained some form of practical accounting experience, for example through work, an internship or a qualified part-time job. Your education may be oriented towards, for example, accounting, auditing, controlling or financial reporting. The most important thing is that you enjoy understanding how things fit together, like collaborating with others and want to develop together with customers and colleagues.

We believe that you have:

• An interest in group and lease accounting and the related frameworks

• An interest in systems, processes, digitalisation and AI

• Good analytical ability and a desire to understand how things fit together

• An ease in communicating and collaborating with others

• Good knowledge of Swedish and English

**Who are you as a person?**

You are a curious, meticulous and solution-oriented team player. You enjoy learning new things and are able to take on both details and the bigger picture.

Since the role involves customer contact, it is important that you are service-minded, pedagogical and confident in dialogue with others. You also need to enjoy working in a structured way, taking responsibility and daring to ask questions when something needs to be understood in depth.

We believe you will thrive with us if you want to work in a smaller specialist company where you are close to customers, colleagues and system development alike.

**Why Tiego?**

At Tiego, our goal is to build the company and workplace where we ourselves want to stay for a long time. This is partly about development opportunities through knowledge sharing, modern technology, smart solutions and challenging customer projects. It is equally about a work environment where we see and respect each other, help each other out and have fun together. It has worked well so far!

We are now facing an exciting future where we are transforming our earlier Excel-based solutions into modern web applications. Here, AI gives us new opportunities to improve efficiency and develop based on our specialist expertise. A growth journey we want to make together with more driven colleagues.

**Scope and location**

The position is a full-time role based primarily at our newly opened office in Norrköping. The office is led by Rasmus Björn, and initially the on-site team will consist of Rasmus and you, our new colleague. This gives you the opportunity to be there from the start and help shape both our ways of working and Tiego's presence in Norrköping.

At the same time, you become part of the whole Tiego team and work closely with our colleagues in Stockholm. You will therefore work from the Stockholm office on average one day per week, including for knowledge sharing, joint work on customer projects and other activities together with the team. The need may vary somewhat over time. Tiego covers the cost of the travel.

**Interested?**

Does this sound like the job for you? Send in your application with a CV and personal letter.

What attracts you most about a role where accounting, systems and customer contact meet? We would love for you to tell us about that in your personal letter.

Your application is received by our HR manager Katarina von Wachenfeldt by email at katarina.wachenfeldt@tiego.se. Katarina reviews the applications and conducts interviews on an ongoing basis together with Rasmus Björn, who is responsible for our office in Norrköping.

We are handling this recruitment with our own resources and kindly decline all contact with staffing and recruitment agencies, as well as sellers of additional job advertisements or recruitment services.`,
  },
  {
    id: "33",
    title: "Tiego medverkar i KEF utbildning om RKR R5",
    titleEn: "Tiego takes part in KEF training on RKR R5",
    excerpt:
      "Den 10 juni medverkar Tiego tillsammans med Kommunalekonomernas förening i utbildningen om hur kommuner praktiskt arbetar med RKR R5 – för kommuner som vill ta nästa steg i leasingredovisningen, bort från förenklingar och mot en tillämpning som bättre speglar regelverkets ambition.",
    excerptEn:
      "On 10 June, Tiego takes part, together with Kommunalekonomernas förening, in the training on how municipalities work in practice with RKR R5 – for municipalities that want to take the next step in lease accounting, away from simplifications and towards an application that better reflects the ambition of the framework.",
    categories: ["TLE"],
    date: "8 jun 2026",
    metaDescription: "Den 10 juni medverkar Tiego i KEF:s utbildning om hur kommuner praktiskt arbetar med RKR R5 – bort från förenklingar, mot regelverkets ambition.",
    metaDescriptionEn: "On 10 June Tiego joins KEF's training on how municipalities work with RKR R5 in practice – away from simplifications, towards the framework's ambition.",
    image: "news/kevin.webp",
    imageStyle: "cover",
    imageAlt: "Porträtt av Kevin Hall, koncernredovisningskonsult på Tiego",
    imageAltEn: "Portrait of Kevin Hall, group accounting consultant at Tiego",
    content: `Den 10 juni medverkar Tiego tillsammans med Kommunalekonomernas förening i utbildningen **"Så arbetar kommuner praktiskt med RKR R5"**.

Utbildningen riktar sig till kommuner som vill ta nästa steg i arbetet med leasingredovisning – bort från förenklingar och mot en tillämpning som bättre speglar regelverkets ambition.

**RKR R5 syftar till att skapa en mer jämförbar redovisning mellan kommuner genom att leasingavtal identifieras, klassificeras och dokumenteras enligt gemensamma principer.** **Samtidigt visar erfarenheterna från kommunsektorn att det fortfarande finns många olika vägval kring marknadsvärdering, räntesättning och praktisk tillämpning.**

Inför utbildningen har Tiego intervjuat flera deltagande kommuner om deras arbete med RKR R5. Samtalen visar att kommunerna befinner sig i mycket olika faser. Några har ännu inte påbörjat arbetet fullt ut, andra har fokuserat på fordon, maskiner och ett mindre antal fastighetsavtal, medan vissa har analyserat hela avtalsportföljen med lokaler, fordon, infrastruktur och koncerninterna avtal.

Fem teman återkommer i nästan samtliga intervjuer:

• Hur har kommunen valt att avgränsa arbetet och vilka avtal omfattas av analysen?

• Hur fastställs marknadsvärden och vilken metod används i praktiken?

• Hur stor insyn har kommunen i de beräkningar som ligger bakom redovisningen?

• Hur ska de senaste förtydligandena och signalerna från RKR tolkas?

• Vilka frågor ställer revisorerna och vilka erfarenheter finns från andra kommuner?

Samtalen visar också att många kommuner efterfrågar erfarenhetsutbyte kring marknadsvärdering, implicit ränta, klassificering av lokalhyresavtal och hur man dokumenterar sina bedömningar på ett sätt som skapar trygghet för både ekonomiavdelning och revisorer.

Under utbildningen går Tiego igenom de grundläggande principerna i RKR R5, de senaste förtydligandena från RKR samt erfarenheter från kommuner som valt olika vägar i sitt arbete med leasingredovisning. Vi kommer även att visa hur samma kontrakt kan analyseras i Excel och i Tiego Lease Engine, med fokus på marknadsvärden, räntor, klassificering, bokföring och underlag till årsredovisningen.

En särskild del av utbildningen ägnas åt de frågor som kommunerna själva lyft fram i intervjuerna. Vår ambition är att skapa en öppen diskussion där deltagarna får möjlighet att jämföra arbetssätt, diskutera utmaningar och ta del av erfarenheter från andra kommuner.

Vi ser fram emot en lärorik förmiddag tillsammans med kommuner från hela landet.

**Läs mer och anmäl dig via Kommunalekonomernas förening:** [Så arbetar kommuner praktiskt med RKR R5 – Kurser och evenemang – KEF](https://www.kef.se/utbildningar/sa-arbetar-kommuner-praktiskt-med-rkr-r5)`,
    contentEn: `On 10 June, Tiego takes part, together with Kommunalekonomernas förening, in the training session **"How municipalities work in practice with RKR R5"**.

The training is aimed at municipalities that want to take the next step in their lease accounting work – moving away from simplifications and towards an application that better reflects the ambition of the framework.

**RKR R5 aims to create more comparable accounting between municipalities by having lease agreements identified, classified and documented according to common principles.** **At the same time, experience from the municipal sector shows that there are still many different approaches to market valuation, interest rate setting and practical application.**

Ahead of the training, Tiego has interviewed several participating municipalities about their work with RKR R5. The conversations show that municipalities are at very different stages. Some have not yet fully begun the work, others have focused on vehicles, machinery and a smaller number of property agreements, while some have analysed their entire contract portfolio, including premises, vehicles, infrastructure and intra-group agreements.

Five themes recur in almost all of the interviews:

• How has the municipality chosen to delimit the work, and which agreements are covered by the analysis?

• How are market values determined, and which method is used in practice?

• How much insight does the municipality have into the calculations behind the accounting?

• How should the latest clarifications and signals from RKR be interpreted?

• What questions do the auditors ask, and what experiences exist from other municipalities?

The conversations also show that many municipalities are looking for an exchange of experience around market valuation, implicit interest rates, the classification of premises rental agreements and how to document their assessments in a way that creates confidence for both the finance department and the auditors.

During the training, Tiego goes through the basic principles of RKR R5, the latest clarifications from RKR and experiences from municipalities that have chosen different paths in their lease accounting work. We will also show how the same contract can be analysed in Excel and in Tiego Lease Engine, focusing on market values, interest rates, classification, bookkeeping and supporting documentation for the annual report.

A special part of the training is devoted to the questions that the municipalities themselves raised in the interviews. Our ambition is to create an open discussion where participants get the opportunity to compare working methods, discuss challenges and take part in experiences from other municipalities.

We look forward to an instructive morning together with municipalities from across the country.

**Read more and register via Kommunalekonomernas förening:** [How municipalities work in practice with RKR R5 – Courses and events – KEF](https://www.kef.se/utbildningar/sa-arbetar-kommuner-praktiskt-med-rkr-r5)`,
  },
  {
    id: "32",
    title:
      "Junior utvecklare till Tiego – utveckla affärskritiska system och väx med oss",
    titleEn:
      "Junior Developer at Tiego – Build Business-Critical Systems and Grow With Us",
    excerpt:
      "Uppdatering: Tjänsten är nu tillsatt – stort tack till alla som sökt! Vi sökte en junior fullstack-utvecklare som vill vidareutveckla våra system för koncernredovisning och leasinghantering.",
    excerptEn:
      "Update: The position has now been filled – a big thank you to everyone who applied! We were looking for a junior full-stack developer to help develop our systems for group accounting and lease management.",
    categories: ["Tiego"],
    date: "8 jun 2026",
    heading: "Junior utvecklare till Tiego",
    headingEn: "Junior Developer at Tiego",
    metaDescription: "Tjänsten är tillsatt. Tiego sökte en junior fullstack-utvecklare som vill vidareutveckla våra system för koncernredovisning och leasinghantering.",
    metaDescriptionEn: "The position has been filled. Tiego was looking for a junior full-stack developer to help develop our systems for group accounting and lease management.",
    image: "news/fullstack.webp",
    imageStyle: "cover",
    imageAlt: "Laptop med C#-kod och kommentaren att Tiego söker fullstack-utvecklare",
    imageAltEn: "Laptop showing C# code with a comment that Tiego is hiring full-stack developers",
    content: `**Uppdatering 25 augusti 2026:** Tjänsten är nu tillsatt. Vi vill rikta ett stort tack till alla som har sökt och visat intresse för Tiego.

Vill du växa som utvecklare och samtidigt få arbeta med system som används av några av Nordens största företag?

Vi söker nu en junior fullstack-utvecklare som vill vara med och vidareutveckla våra produkter inom koncernredovisning och leasinghantering. Du kommer att arbeta med moderna webbapplikationer och få möjlighet att utvecklas inom frontend, backend, databaser och AI-assisterad utveckling.

Tjänsten är en heltidsanställning med start den 1 september och är initialt en sex månader lång projektanställning för att täcka upp under en föräldraledighet. Vår ambition är att rollen ska kunna övergå i en tillsvidareanställning om både du och vi trivs tillsammans.

**Om Tiego**

Tiego utvecklar affärssystem för koncernredovisning och leasinghantering. Våra produkter används av börsnoterade bolag, fastighetskoncerner, industriföretag och offentliga verksamheter för affärskritiska processer där kvalitet, spårbarhet och tillförlitlighet är avgörande.

Bakom användargränssnitten finns avancerade beräkningar, stora datamängder och komplex affärslogik. Som utvecklare hos oss får du därför möjlighet att arbeta med både teknik och verksamhetsnära problemställningar.

Vi är idag sex utvecklare och arbetar i nära samarbete med produktansvariga, konsulter och kunder. Det innebär korta beslutsvägar, stort inflytande över produkterna och möjlighet att göra skillnad från första dagen. Du blir en del av utvecklingsteamet och får vara med och vidareutveckla lösningar som används i affärskritiska processer hos några av Nordens största företag.

**Om rollen**

Under hösten kommer en större del av arbetet att vara kopplat till våra webbgränssnitt, samtidigt som du får möjlighet att lära dig backendutveckling i C#/.NET, databaser och produktutveckling. Du kommer att få stöd av erfarna utvecklare men också uppmuntras att ta egna initiativ och successivt ta större ansvar.

Exempel på arbetsuppgifter:

• Utveckla nya funktioner i våra webbapplikationer

• Arbeta med Angular och TypeScript

• Delta i design och utveckling av användargränssnitt

• Bidra till backendutveckling i C#/.NET

• Arbeta med databaser och SQL Server

• Felsöka, förbättra och vidareutveckla befintliga lösningar

• Utforska hur AI kan användas för att effektivisera utveckling och skapa nya funktioner i våra produkter

**Teknik**

Vi arbetar bland annat med:

• Angular

• TypeScript

• C# / .NET

• SQL Server

• Azure

• Git

AI är en naturlig del av vårt dagliga utvecklingsarbete. Vi använder AI-stöd för att effektivisera utveckling, kvalitetssäkring och problemlösning samtidigt som vi utforskar hur AI kan skapa värde för våra kunder genom nya funktioner i produkterna.

Du behöver inte vara expert på AI, men vi tror att du är nyfiken på hur tekniken kan användas i praktiken.

**Vem är du?**

Vi tror att du:

• Studerar eller nyligen har avslutat en utbildning inom systemutveckling eller liknande

• Har erfarenhet av Angular, TypeScript eller annan modern frontendutveckling

• Har grundläggande kunskaper i C#/.NET

• Gillar problemlösning och vill förstå hur system fungerar på djupet

• Är nyfiken, ansvarstagande och vill utvecklas som utvecklare

• Kommunicerar väl på svenska och engelska

Det är meriterande om du har erfarenhet av SQL, Azure eller AI-relaterade projekt.

Vi förväntar oss inte att du kan allt från start. Det viktigaste är att du är nyfiken, vill lära dig och tycker om att lösa problem. Om du känner igen dig i beskrivningen men inte uppfyller alla punkter uppmuntrar vi dig ändå att söka.

**Varför Tiego?**

Hos oss får du:

• Lära dig av erfarna kollegor och snabbt få eget ansvar

• Erfarenhet av affärssystem och komplex problemlösning

• Möjlighet att påverka produkter som används av stora organisationer

• Flexibla arbetstider och hybridarbete

• Centralt kontor på Sveavägen i Stockholm

• Möjlighet att arbeta med modern AI-assisterad utveckling

• Goda möjligheter till fortsatt anställning efter projektperioden

Vi tror på att ge människor ansvar tidigt och låta idéer väga tyngre än titel eller erfarenhet. För oss är nyfikenhet, samarbete och vilja att utvecklas minst lika viktigt som teknisk erfarenhet.

**Intresserad?**

Skicka CV och några rader om dig själv till Katarina von Wachenfeldt, katarina.wachenfeldt@tiego.se.

Vi intervjuar löpande och ser fram emot att höra från dig.`,
    contentEn: `**Update 25 August 2026:** The position has now been filled. We would like to extend a big thank you to everyone who applied and showed interest in Tiego.

Do you want to grow as a developer while working with systems used by some of the largest companies in the Nordics?

We are now looking for a junior full-stack developer who wants to help further develop our products for group accounting and lease management. You will work with modern web applications and have the opportunity to grow within frontend, backend, databases and AI-assisted development.

The position is a full-time role starting on 1 September and is initially a six-month project employment to cover a parental leave. Our ambition is for the role to transition into a permanent position if both you and we thrive together.

**About Tiego**

Tiego develops business systems for group accounting and lease management. Our products are used by listed companies, real estate groups, industrial companies and public sector organisations for business-critical processes where quality, traceability and reliability are essential.

Behind the user interfaces are advanced calculations, large data volumes and complex business logic. As a developer with us, you therefore get the opportunity to work with both technology and business-oriented problems.

Today we are six developers, working in close collaboration with product owners, consultants and customers. This means short decision paths, significant influence over the products and the opportunity to make a difference from day one. You become part of the development team and help further develop solutions used in business-critical processes at some of the largest companies in the Nordics.

**About the role**

During the autumn, a larger part of the work will be connected to our web interfaces, while you also get the opportunity to learn backend development in C#/.NET, databases and product development. You will receive support from experienced developers but are also encouraged to take your own initiative and gradually take on greater responsibility.

Examples of tasks:

• Develop new features in our web applications

• Work with Angular and TypeScript

• Take part in the design and development of user interfaces

• Contribute to backend development in C#/.NET

• Work with databases and SQL Server

• Troubleshoot, improve and further develop existing solutions

• Explore how AI can be used to make development more efficient and to create new features in our products

**Technology**

Among other things, we work with:

• Angular

• TypeScript

• C# / .NET

• SQL Server

• Azure

• Git

AI is a natural part of our daily development work. We use AI support to make development, quality assurance and problem solving more efficient, while exploring how AI can create value for our customers through new features in the products.

You do not need to be an expert in AI, but we believe you are curious about how the technology can be used in practice.

**Who are you?**

We believe that you:

• Are studying, or have recently completed, an education in systems development or similar

• Have experience of Angular, TypeScript or other modern frontend development

• Have basic knowledge of C#/.NET

• Enjoy problem solving and want to understand how systems work in depth

• Are curious, responsible and want to develop as a developer

• Communicate well in Swedish and English

It is an advantage if you have experience of SQL, Azure or AI-related projects.

We do not expect you to know everything from the start. The most important thing is that you are curious, want to learn and enjoy solving problems. If you recognise yourself in the description but do not meet all the points, we still encourage you to apply.

**Why Tiego?**

With us you get:

• To learn from experienced colleagues and quickly take on your own responsibility

• Experience of business systems and complex problem solving

• The opportunity to influence products used by large organisations

• Flexible working hours and hybrid work

• A central office on Sveavägen in Stockholm

• The opportunity to work with modern AI-assisted development

• Good prospects for continued employment after the project period

We believe in giving people responsibility early and letting ideas carry more weight than title or experience. For us, curiosity, collaboration and the will to develop are at least as important as technical experience.

**Interested?**

Send your CV and a few words about yourself to Katarina von Wachenfeldt, katarina.wachenfeldt@tiego.se.

We interview on an ongoing basis and look forward to hearing from you.`,
  },
  {
    id: "31",
    title: "Tiego utmanar kommuner att ta nästa steg i RKR R5 – bort från förenkling, mot tillämpning enligt regelverkets ambition",
    excerpt:
      "Den 7 maj deltar Tiego på Örebro läns ekonomichefsdagar med en fokuserad timme kring RKR R5. Ambitionen är att skapa ett konkret utbyte kring hur kommuner kan gå från förenklade lösningar till en mer genomarbetad och hållbar tillämpning av regelverket.",
    categories: ["TLE"],
    date: "5 maj 2026",
    heading: "Tiego utmanar kommuner om RKR R5",
    metaDescription: "Den 7 maj deltar Tiego på Örebro läns ekonomichefsdagar med en timme om RKR R5 – hur kommuner går från förenklade lösningar till hållbar tillämpning.",
    image: "news/kevin-rkr.webp",
    imageStyle: "portrait",
    imageAlt: "Kevin Hall, Tiego, framför kameran i studiomiljö",
    imageAltEn: "Kevin Hall, Tiego, in front of the camera in a studio setting",
    content: `**En fokuserad timme om RKR R5 i Örebro**

Den 7 maj deltar Tiego på Örebro läns ekonomichefsdagar med en fokuserad timme kring RKR R5. Ambitionen är att skapa ett konkret utbyte kring hur kommuner kan gå från förenklade lösningar till en mer genomarbetad och hållbar tillämpning av regelverket.

Många kommuner och regioner har lagt ett omfattande arbete på sin redovisning enligt RKR R5. Under timmen kommer en ekonomichef att gå igenom hur arbetet har sett ut i praktiken, med utgångspunkt i föregående års årsredovisning, årets vidareutveckling och den feedback som lämnats av revisorer. Det ger en konkret bild av både vad som fungerar och var det fortfarande finns utmaningar.

**Diskonteringsräntan – implicit före marginell**

En central del är frågan om diskonteringsränta. I praktiken har många kommuner landat i att använda den marginella låneräntan som standard. Tiego vill bidra till ett arbetssätt där man i större utsträckning försöker nå fram till en implicit ränta, i linje med regelverkets ambition.

"RKR R5 pekar tydligt mot implicit ränta där det är möjligt. Vår erfarenhet är att många kommuner kan komma dit, men det kräver att man arbetar mer strukturerat med sina parametrar och vågar analysera sina kontrakt på djupet. Den marginella låneräntan ska vara en utväg, inte ett förstahandsval," säger Kevin Hall, produktansvarig för Tiego Lease Engine.

**Fem steg från inventering till analys**

Under timmen går vi igenom ett arbetssätt i fem steg som sträcker sig från inventering och värdering till beräkning, dokumentation och analys av redovisningseffekter. Fokus ligger på att skapa kvalitet i beräkningarna och trygghet i de bedömningar som görs.

Det handlar samtidigt inte bara om metod utan också om arbetssätt. En viktig del är att ekonomer själva kan arbeta med sina leasingdata, förstå sina beräkningar och successivt bygga upp kompetens över tid.

**Stöd genom Tiego Lease Engine**

Som avslutning visas hur arbetet kan struktureras i praktiken med stöd av Tiego Lease Engine, där kontrakt, antaganden och beräkningar samlas i ett sammanhållet flöde med spårbarhet hela vägen till rapportering och revision.

**Liknande upplägg för fler ekonomichefsnätverk**

Tiego erbjuder liknande upplägg till andra ekonomichefsnätverk som vill arbeta mer praktiskt med RKR R5. Fokus ligger på erfarenhetsutbyte mellan kommuner, analys av verkliga exempel och stöd i att utveckla en mer robust tillämpning av regelverket.

Målet är att ge ekonomifunktioner bättre förutsättningar att arbeta i linje med regelverkets ambition och samtidigt skapa en effektiv och hållbar process över tid.`,
  },
  {
    id: "29",
    title: "Att anställa potential – vad gör man när kandidaten saknar CV?",
    titleEn: "Hiring for Potential. What Do You Do When the Candidate Has No CV?",
    excerpt:
      "Mattias Elfgren, vd och redovisningsexpert på Tiego, i en krönika för Ekonomisverige om hur man intervjuar potential när kandidaten ännu inte hunnit samla erfarenheter i ett CV.",
    excerptEn:
      "Mattias Elfgren, CEO and accounting expert at Tiego, in a column for Ekonomisverige on how to interview for potential when the candidate has not yet had time to build up experience in a CV.",
    categories: ["Tiego"],
    date: "19 mar 2026",
    heading: "Att anställa potential",
    headingEn: "Hiring for Potential",
    metaDescription: "Mattias Elfgren, vd på Tiego, i en krönika för Ekonomisverige om hur man intervjuar potential när kandidaten ännu inte har erfarenheter i ett CV.",
    metaDescriptionEn: "Mattias Elfgren, CEO of Tiego, in a column for Ekonomisverige on how to interview for potential when the candidate has no experience on their CV yet.",
    image: "news/mattias-elfgren-tiego.webp",
    imageStyle: "portrait",
    imageAlt: "Mattias Elfgren, VD på Tiego, i samtal vid ett mötesbord",
    imageAltEn: "Mattias Elfgren, CEO of Tiego, in conversation at a meeting table",
    content: `Mattias Elfgren, vd och redovisningsexpert på Tiego, i en krönika för Ekonomisverige.

**Hur intervjuar man egentligen potential?**

När en student ännu inte hunnit samla erfarenheter i ett CV behöver samtalet handla om något annat, om hur personen tänker, lär och samarbetar. På Tiego har vi under de senaste åren försökt utveckla ett arbetssätt för just detta.

Vi säger ofta att vi vill anställa unga och talangfulla studenter från universitet och högskolor. Samtidigt ser intervjun naturligtvis annorlunda ut när kandidaten fortfarande befinner sig i början av sin karriär.

Det finns ännu inga uppdrag att analysera i efterhand, inga större projekt att reflektera över och få situationer där erfarenhet hunnit byggas upp över tid. Ändå ska både arbetsgivare och student försöka förstå om det här kan vara rätt plats att börja ett yrkesliv.

För oss på Tiego har det därför blivit viktigt att låta samtalet handla om något annat än det som brukar stå i ett CV. Mindre om vad en kandidat redan har gjort, mer om hur personen tänker, lär och samarbetar.

**Beslutspunkter i livet säger mer än man tror**

I vårt första möte har vi därför börjat med en ömsesidig genomgång av beslutspunkter i livet. Det kan handla om valet av gymnasium, om att satsa på en sport eller välja bort en annan. Ofta borrar vi lite i inställningen till träning och lagidrott.

Anledningen är inte att alla ska ha en idrottsbakgrund. Snarare är det så att sådana val ofta säger något om disciplin, motivation, uthållighet och samarbete.

Samtidigt är det viktigt att samtalet verkligen är ömsesidigt. Alla som deltar i intervjun delar också sina egna val, tveksamheter och omvägar. Studenten ska inte bara bli bedömd, utan också få en bild av oss som arbetar på Tiego, hur vi tänker, vad som driver oss och vilka förväntningar som faktiskt finns.

**En tenta kan säga mer än ett CV**

I nästa steg har vi infört ett moment som kan låta ovanligt. Kandidaten tar med sig en tenta i redovisning. Inte för att bli förhörd, utan för att gå igenom den tillsammans med oss.

Fokus ligger på att beskriva problemet, hur kunskapen har byggts upp och hur resonemanget lett fram till svaret. För oss skapar det en djupare dialog kring problemlösning och förståelse. För studenten blir det ett samtal på trygg mark.

Samtidigt får vi på Tiego en värdefull inblick i vad som faktiskt lärs ut på våra lärosäten i dag.

**När en extern part ställer frågorna**

Vi har också valt att komplettera våra egna intervjuer med ett steg där en extern rekryteringsspecialist genomför tester och återkoppling. Tanken är inte att låta ett test avgöra vem som får jobbet, utan att få ytterligare ett professionellt perspektiv på hur en person fattar beslut, löser problem och fungerar i samarbete med andra.

I vår senaste rekrytering blev just detta moment mycket uppskattat av kandidaten. Testerna blev ett diskussionsunderlag där vi tillsammans kunde prata om arbetssätt, tempo, noggrannhet och hur man reagerar i olika situationer.

För många unga ekonomer är detta första gången någon ställer mer strukturerade frågor om hur de faktiskt arbetar. Hur reagerar man när det blir stressigt, när något är oklart eller när man behöver samarbeta med personer som arbetar på ett annat sätt än man själv.

För oss som arbetsgivare ger det ett mer objektivt perspektiv än vad vi kan få enbart genom egna samtal. För kandidaten blir det samtidigt en möjlighet att reflektera över sina styrkor och utvecklingsområden på ett sätt som kan vara värdefullt långt efter att rekryteringsprocessen är över.

Vi hoppas förstås att de personer vi anställer stannar hos oss länge. Men även om vägarna någon gång skulle skiljas åt i framtiden känns det bra att veta att de tidigt i karriären fått en seriös återkoppling kring hur de fungerar i professionella sammanhang.

**Lärandet börjar i riktiga projekt**

När vi väl tar in en student märker vi också att onboarding ser annorlunda ut. Att låta någon växa in i rollen på deltid är ofta betydligt smidigare än att ta in en nyutexaminerad på heltid och därefter försöka hitta rätt sysselsättning.

Utvecklingen blir mer naturlig, både för individen och för organisationen.

En viktig del i detta är hur vi arbetar med workshops för potentiella kunder. Vi skriver NDA, får tillgång till detaljerad data och processar den under cirka tio dagar till en rimligt klar koncernkonsolidering. Det är ett intensivt arbete där juniora och seniora medarbetare arbetar tätt tillsammans.

Kunden är involverad, insikter skapas i realtid och förståelsen för både problem och lösning fördjupas. Systemvalet kommer först efter workshopen.

I de här projekten uppstår en energi som är svår att skapa i mer teoretiska sammanhang. För studenter och nyutexaminerade blir det lärande på riktigt. För oss blir det ett sätt att utveckla kompetens och se människor växa. För kunden blir det samtidigt ett tillfälle att lära känna både lösningen och teamet, erfarna såväl som yngre medarbetare.

Jag minns fortfarande hur det var att komma ut som ny ung ekonom under min tid som revisor på EY. Viljan att göra rätt, osäkerheten inför de första uppdragen och känslan av att lärandet tog fart först när man faktiskt fick vara med på riktigt.

När jag i dag möter våra egna unga medarbetare hoppas jag att de är modiga. Modiga nog att ibland bli utskickade på hal is, men alltid med erfarenhet runt omkring sig. Där brukar utvecklingen ta fart.

I dag finns dessutom något vi inte hade då. Med AI är stora mängder information, exempel och analys ständigt tillgängliga. Använd på rätt sätt kan det bli ett stöd för reflektion och förståelse, och en kraftfull hävstång för att bygga kompetens snabbare. Inte som ersättning för omdöme, utan som ett verktyg i lärandet.

För mig landar detta i en enkel uppmaning till oss som bransch. Vi behöver bli bättre på att se uppsidan med studenter och nyutexaminerade, inte bara som ett framtidslöfte utan som en möjlighet här och nu.

När man tänker tillbaka på sin egen studietid blir det tydligt hur värdefullt det hade varit att tidigt få arbeta nära erfarna kollegor i skarpa situationer.

Kanske är det just där vi behöver börja om vi menar allvar med kompetensförsörjning och professionens utveckling. Att våga anställa potential, långt innan den hunnit bli ett CV.`,
    contentEn: `Mattias Elfgren, CEO and accounting expert at Tiego, in a column for Ekonomisverige.

**How do you actually interview for potential?**

When a student has not yet had time to build up experience in a CV, the conversation needs to focus on something else, namely how the person thinks, learns and collaborates. At Tiego, we have spent the past few years trying to develop an approach for exactly this.

We often say that we want to hire young and talented students from universities and colleges. At the same time, the interview process naturally looks different when the candidate is still at the very beginning of their career.

There are no past assignments to analyse, no major projects to reflect on and few situations where experience has had time to build over the years. Yet both employer and student need to understand whether this could be the right place to start a professional journey.

For us at Tiego, it has therefore become important to let the conversation focus on something other than what is typically found in a CV, less on what a candidate has already done and more on how they think, learn and work with others.

**Life decisions reveal more than you might think**

In our first meeting, we have started with a mutual discussion about key decision points in life. This might include choices such as which high school to attend, whether to pursue a sport seriously or to step away from one. We often explore attitudes toward training and team sports in a bit more depth.

The point is not that everyone needs a background in sports. Rather, these kinds of choices often say something about discipline, motivation, and the ability to stay focused over time, as well as teamwork.

At the same time, it is important that the conversation is genuinely two-way. Everyone involved in the interview also shares their own choices, doubts and detours. The student should not only be evaluated, but also gain a sense of who we are at Tiego, how we think, what drives us and what expectations actually look like in practice.

**An exam can say more than a CV**

In the next step, we introduce something that may sound a bit unusual. The candidate brings along an accounting exam, not to be tested, but to review it together with us.

The focus is on explaining the problem, how the knowledge was built and how the reasoning led to the final answer. For us, this creates a deeper dialogue around problem solving and understanding. For the student, it becomes a conversation on familiar ground.

At the same time, we gain valuable insight into what is actually being taught at universities today.

**When an external perspective asks the questions**

We also complement our own interviews with a step where an external recruitment specialist conducts assessments and provides feedback. The idea is not to let a test decide who gets the job, but to gain an additional professional perspective on how a person makes decisions, solves problems and collaborates with others.

In our most recent recruitment, this step was highly appreciated by the candidate. The assessments became a basis for discussion, where we could talk together about working styles, pace, attention to detail and how one responds in different situations.

For many young finance professionals, this is the first time someone asks more structured questions about how they actually work, how they react under pressure, when things are unclear or when they need to collaborate with people who work differently from themselves.

For us as an employer, this provides a more objective perspective than we can get from our own conversations alone. For the candidate, it is an opportunity to reflect on strengths and areas for development in a way that can remain valuable long after the recruitment process is over.

We of course hope that the people we hire will stay with us for a long time. Even if our paths were to diverge in the future, it feels meaningful to know that they early in their careers received thoughtful and professional feedback on how they function in a professional setting.

**Learning begins in real projects**

Once we bring a student on board, we also find that onboarding looks different. Letting someone grow into the role part time is often much smoother than hiring a recent graduate full time and then trying to find the right tasks afterward.

The development becomes more natural, both for the individual and for the organisation.

An important part of this is how we work with workshops for prospective clients. We sign NDAs, gain access to detailed data and process it over roughly ten days into a reasonably complete group consolidation. It is an intense process where junior and senior colleagues work closely together.

The client is involved, insights are created in real time and the understanding of both the problem and the solution deepens. The system decision comes only after the workshop.

In these projects, an energy emerges that is hard to replicate in more theoretical settings. For students and recent graduates, it becomes real learning. For us, it is a way to build capability and see people grow. For the client, it is also an opportunity to get to know both the solution and the team, experienced professionals as well as younger colleagues.

I still remember what it was like starting out as a young finance professional during my time as an auditor at EY, the desire to do things right, the uncertainty before the first assignments and the feeling that learning only truly accelerated once you were part of real work.

When I meet our own junior colleagues today, I hope they are brave, brave enough to sometimes be pushed slightly out of their comfort zone, but always surrounded by experience. That is usually where development takes off.

Today, there is also something we did not have back then. With AI, vast amounts of information, examples and analysis are constantly available. Used in the right way, it can support reflection and understanding and act as a powerful lever for building competence faster. It is not a substitute for judgement, but a tool for learning.

For me, this leads to a simple message to our industry. We need to get better at recognising the value of students and recent graduates, not only as a future promise, but as an opportunity here and now.

Looking back at one's own student years, it becomes clear how valuable it would have been to work closely with experienced colleagues in real situations early on.

Perhaps that is where we need to begin if we are serious about talent development and the future of the profession, to dare to hire potential long before it has had time to become a CV.`,
  },
  {
    id: "30",
    title: "Revisorns hjärta, AI:s hjärna – och varför fler ekonomer borde tänka som skeptiker",
    titleEn: "The Auditor's Heart, the Brain of AI, and Why More Finance Professionals Should Think Like Skeptics",
    excerpt:
      "Mattias Elfgren, vd och redovisningsexpert på Tiego, i en krönika för Ekonomisverige om varför skepsis, nyfikenhet och helikopterperspektiv är viktigare än någonsin i AI:s tidevarv.",
    excerptEn:
      "Mattias Elfgren, CEO and accounting expert at Tiego, in a column for Ekonomisverige on why skepticism, curiosity and a helicopter perspective have become more important than ever in the age of AI.",
    categories: ["Tiego"],
    date: "17 dec 2025",
    heading: "Revisorns hjärta, AI:s hjärna",
    headingEn: "The Auditor's Heart, the Brain of AI",
    metaDescription: "Mattias Elfgren, vd på Tiego, i en krönika för Ekonomisverige om varför skepsis, nyfikenhet och helikopterperspektiv är viktigare än någonsin i AI:s tid.",
    metaDescriptionEn: "Mattias Elfgren, CEO of Tiego, writes in Ekonomisverige on why skepticism, curiosity and a helicopter perspective matter more than ever in the age of AI.",
    image: "news/mattias-elfgren.webp",
    imageStyle: "portrait",
    imageAlt: "Porträtt av Mattias Elfgren, VD på Tiego",
    imageAltEn: "Portrait of Mattias Elfgren, CEO of Tiego",
    content: `Mattias Elfgren, vd och redovisningsexpert på Tiego i en krönika för Ekonomisverige.

**Att vara ekonom har alltid handlat om att förstå helheten bakom siffrorna**

Men i en tid när AI både utmanar och förstärker vår analysförmåga blir skepsis, nyfikenhet och ett helikopterperspektiv viktigare än någonsin. Här är vad jag har lärt mig av en CFO med falköga och varför erfarenhet och AI tillsammans kan lyfta yrkesrollen till nya nivåer.

Det finns en egenskap jag ofta återkommer till i mitt arbete på Tiego: nyttan av att tänka som en revisor, även när man inte är en. Det handlar om den där naturliga skepsisen inför siffror, men också om förmågan att lyfta blicken, se helheten och ställa sig frågan om siffrorna verkligen beskriver den utveckling man förväntar sig. För den som är djupt involverad i siffrorna är det just helikopterperspektivet som håller berättelsen samman.

Jag brukar ibland skämtsamt säga att vissa ekonomer förväntar sig att siffrorna är rätt tills någon bevisar motsatsen. Revisorer däremot förväntar sig att de är fel tills motsatsen är bevisad. Den mentaliteten är otroligt värdefull, särskilt nu när AI kliver in i ekonomens vardag. Det är samma grundförmåga som förenar allt gott analysarbete: att kunna se mönster, avvikelser och logik i ett större sammanhang.

**Autoliv och lyxen att arbeta mitt i tillväxtens och förvärvens logik**

Efter studierna och några år på EY fick jag möjligheten att arbeta som koncernekonom på Autoliv i fyra år. Det var en tid av förvärv, kraftig tillväxt och en rapporteringsmiljö där siffrorna förändrades snabbt. För mig blev det en av de mest formativa perioderna i min karriär.

CFO:n då, Wilhelm Kull, var ett fenomen. Vi kallade honom "falköga".

För honom handlade det aldrig om att hitta fel för felens skull. Det handlade om att förstå helheten och utvecklingen. Han kunde på sekunder se om något inte var logiskt eller rimligt. Hans första fråga var ofta: Visar siffrorna den riktiga utvecklingen, eller kan det finnas något mer här?

Det är svårt att förklara för någon som inte har arbetat nära riktigt skickliga ekonomer hur inspirerande det är. Man lär sig att siffror inte bara ska stämma – de ska hänga ihop och berätta en sammanhållen och trovärdig historia. Och det är när berättelsen haltar som avvikelsen blir intressant.

Den inställningen, att aldrig nöja sig med ytan och alltid kontrollera rimligheten, har följt mig sedan dess. Och det är just den som gör att jag är så fascinerad av hur AI förändrar vårt arbete som ekonomer.

**AI förstärker skeptikerns superkraft**

Det är inte så att AI ersätter vår analysförmåga. Tvärtom. AI förstärker den – men bara om vi använder den utifrån den grundläggande skepsisen och helhetsblicken.

AI är fantastisk på att borra ner i detaljer i blixtfart, men den saknar den mänskliga impulsen att känna när något inte riktigt hänger ihop. Den saknar förmågan att fråga sig: Är det rimligt, givet utvecklingen? Är den här siffran en del av berättelsen eller ett brott i logiken?

I veckan som gick satt jag i ett implementationsprojekt där ett konto hade ovanliga transaktioner. Inte felaktiga, bara annorlunda. Och i ekonomens värld är "annorlunda" ofta viktigare än "fel", eftersom det är där berättelsen riskerar att glida isär.

En snabb drill-down visade att transaktionerna hörde ihop med ett delägt utländskt bolag som egentligen borde hanteras som ett handelsbolag i koncernrapporteringen. Inget dramatiskt, men sättet det var bokfört på riskerade att skapa onödig komplexitet.

Det fina var att jag och kundens ekonomichef kunde bolla detta i realtid med AI – men inte genom att acceptera det första svaret. Tvärtom. Vi matade in mer information, utmanade förslagen och ställde de där revisorsfrågorna som AI inte själv tänker på, de frågor som föds ur erfarenhet, rimlighetsbedömning och ren yrkesmässig intuition.

Till slut landade vi i ett PM som vi kunde presentera för deras redovisningsexperter samma dag. Det hade garanterat tagit flera dagar utan AI, men utan vårt gemensamma falköga hade AI aldrig hittat rätt riktning.

**AI gör inte ekonomer bättre, men det gör bra ekonomer ännu bättre**

Det är här jag tror att ekonomrollen håller på att förändras. AI gör inte jobbet åt oss. AI gör mer av det vi redan gör – snabbare, mer uthålligt och med större kapacitet. Men riktningen – logiken, rimlighetsbedömningen och helhetsberättelsen – måste fortfarande komma från oss.

De ekonomer som kommer att lyckas bäst framöver är de som tänker som skeptiker, älskar att förstå varför något avviker och vågar utmana både sina egna och AI:s slutsatser, och itererar tills siffrorna verkligen berättar en historia som hänger ihop med verkligheten.

Jag tror att det här är ett av de mest spännande skiften som sker i ekonomibranschen just nu. Wilhelm Kull behövde inte AI. Men AI behöver fler som tänker som Wilhelm.

Det är kombinationen av mänsklig intuition och teknisk snabbhet som kommer att definiera framtidens ekonomifunktion. För mig är det kanske det mest stimulerande av allt: att kunna förena erfarenhet och helhetsblick med tillgång till expertis som tidigare bara fanns i läroböcker eller kursmaterial.

När AI utmanar mig, kompletterar mig och öppnar upp nya perspektiv kommer jag snabbare ner på den nivå där siffror, regelverk och verklighet möts. Det ger energi – inte för att jobba mer, utan för att förstå mer. Och i en värld där rätt förståelse är grunden för alla goda beslut är det faktiskt svårt att tänka sig en mer spännande tid att vara ekonom.`,
    contentEn: `Mattias Elfgren, CEO and accounting expert at Tiego, in a column for Ekonomisverige.

**Being a finance professional has always been about understanding the bigger picture behind the numbers.**

In a time when AI both challenges and strengthens our analytical ability, skepticism, curiosity and a helicopter perspective have become more important than ever. Here is what I have learned from a sharp-eyed CFO, and why experience and AI together can take the profession to the next level.

One principle I often return to in my work at Tiego is this: the value of thinking like an auditor, even if you are not one. It is about that natural skepticism towards numbers, but also about the ability to step back, see the bigger picture and ask whether the numbers truly reflect the development you would expect. For those deeply involved in the details, it is this broader perspective that holds the story together.

I sometimes joke that some finance professionals assume the numbers are correct until proven otherwise. Auditors tend to assume the opposite, that something may be off until it has been properly verified. That mindset is incredibly valuable, especially now that AI is becoming part of everyday work. It reflects a core capability behind all strong analysis: the ability to see patterns, deviations and logic in a broader context.

**Autoliv and the privilege of working at the heart of growth and acquisitions**

After my studies and a few years at EY, I had the opportunity to work as a group accountant at Autoliv for four years. It was a period marked by acquisitions, strong growth and a reporting environment where numbers changed quickly. For me, it became one of the most formative phases of my career.

The CFO at the time, Wilhelm Kull, was exceptional. We called him "hawk eye".

For him, it was never about finding errors for the sake of it. It was about understanding the bigger picture and the underlying development. He could tell within seconds if something did not make sense. His first question was often: do these numbers reflect the real development, or is there something more going on?

It is difficult to explain to someone who has not worked closely with highly skilled finance professionals how inspiring that is. You learn that numbers should not only be correct, they should connect and form a coherent and credible story. And it is when that story starts to break down that deviations become interesting.

That mindset, never settling for the surface and always testing what is reasonable, has stayed with me ever since. It is also why I find the impact of AI on our profession so fascinating.

**AI strengthens the skeptic's superpower**

AI does not replace our analytical ability. On the contrary, it amplifies it, but only if we use it with the same underlying skepticism and sense of the bigger picture.

AI is extremely good at drilling into details at speed, but it lacks the human instinct to sense when something does not quite add up. It does not naturally ask: is this reasonable, given the development? Is this number part of the story, or does it break the logic?

Earlier this week, I was involved in an implementation project where an account contained unusual transactions. Not incorrect, just different. In finance, "different" is often more important than "wrong", because that is where the story risks losing coherence.

A quick drill-down showed that the transactions related to a partially owned foreign entity that should, in substance, be treated as a partnership in group reporting. Nothing dramatic, but the way it had been recorded risked creating unnecessary complexity.

What worked particularly well was that I and the client's finance manager could explore this in real time using AI, not by accepting the first answer, but by adding more context, challenging the suggestions and asking the kinds of questions that AI does not raise on its own. These are the questions that come from experience, judgment and professional intuition.

By the end of the day, we had a memo ready to present to their accounting specialists. It would likely have taken several days without AI, but without our shared "hawk eye", AI would not have found the right direction.

**AI does not make finance professionals better, but it makes good ones even better**

This is where I believe the role of the finance professional is evolving. AI does not do the job for us. It does more of what we already do, faster, more consistently and with greater capacity. The direction, however, the logic, the sense of what is reasonable and the overall narrative, must still come from us.

The finance professionals who will succeed going forward are those who think like skeptics, who are curious about why something deviates and are willing to challenge both their own conclusions and those suggested by AI, and who keep iterating until the numbers truly reflect a story that aligns with reality.

I believe this is one of the most exciting shifts currently taking place in our profession. Wilhelm Kull did not need AI. But AI needs more people who think like Wilhelm.

It is the combination of human intuition and technological speed that will define the finance function of the future. For me, this is perhaps the most stimulating aspect of all, the ability to combine experience and a broad perspective with access to expertise that previously existed mainly in textbooks or training materials.

When AI challenges me, complements me and opens up new perspectives, I reach more quickly the point where numbers, regulation and reality meet. That creates energy, not to work more, but to understand more. In a world where the right understanding underpins every good decision, it is hard to imagine a more exciting time to work in finance.`,
  },
  {
    id: "27",
    title: "Hur bör kommuner och regioner formulera en RFI för koncernrapportering?",
    excerpt:
      "Utifrån flera aktuella RFI-processer delar Tiego erfarenheter kring vilka frågeställningar som skapar värde vid upphandling av koncernrapporteringssystem för kommuner och regioner.",
    categories: ["TGA"],
    date: "15 jan 2026",
    heading: "RFI för koncernrapportering",
    metaDescription: "Utifrån aktuella RFI-processer delar Tiego erfarenheter om vilka frågor som skapar värde vid upphandling av koncernrapporteringssystem för kommuner.",
    
    image: "news/kef-logo.png",
    imageStyle: "contain",
    imageAlt: "Kommunalekonomernas förening (KEF) logotyp",
    imageAltEn: "Association of Municipal Economists (KEF) logo",
    images: {
      kef: "news/kef-logo.png",
    },
    content: `**Hur bör kommuner och regioner formulera en RFI för koncernrapportering?**

När kommuner och regioner står inför att investera i ett nytt koncernrapporteringssystem är RFI-steget ofta avgörande för hur väl upphandlingen faller ut. Ambitionen är hög: rätt krav ska ställas, kvalitet säkras och ett stabilt beslutsunderlag tas fram. Samtidigt är det ett moment som lätt blir svårt. Vilka frågor ger egentligen underlag för ett klokt systemval, och hur undviker man att RFI:n reduceras till en lång lista av funktioner utan tydlig koppling till det faktiska arbetet i organisationen?

Utifrån flera aktuella RFI-processer som Tiego har deltagit i ser vi tydliga mönster i vilka frågeställningar som skapar värde, vilka som ofta saknas och hur beskrivningen av nuläget påverkar kvaliteten i leverantörernas svar.

**Strukturera RFI:n efter hur arbetet faktiskt ser ut**

En gemensam nämnare i de mest träffsäkra RFI:erna är att de är strukturerade utifrån hur ett koncernbokslut faktiskt tas fram, snarare än utifrån hur ett enskilt system råkar vara uppbyggt. När frågorna delas in i tydliga sektioner blir svaren mer relevanta och beslutsunderlaget mer jämförbart.

I praktiken rör det sig ofta om tre huvudområden:

• Inrapportering och integration, där frågor om transaktioner eller saldon, rapporteringsfrekvens, mappning av kontoplaner och stöd för olika ekonomisystem får stor betydelse för både kvalitet och arbetsbelastning.

• Systemstöd för koncernredovisning, exempelvis hur konsolidering i flera nivåer hanteras, hur delägda bolag och ägarförändringar påverkar resultat och balans, samt hur elimineringar, klyvning, noter och historik hanteras över tid.

• Teknik, säkerhet och drift, såsom moln- eller lokal installation, land för databasen, GDPR, uppdateringsrutiner, tillgänglighet och support.

När dessa områden hålls isär och ges ett tydligt syfte blir det lättare att både formulera frågor och utvärdera svaren på ett strukturerat sätt.

**Ta ställning till var konsolideringen ska ske**

En fråga som visat sig ha mycket stor betydelse för både systemval och framtida arbetssätt är var konsolideringen faktiskt ska ske. I flera RFI:er är detta otydligt eller underförstått, trots att valet får långtgående konsekvenser.

Kommuner och regioner behöver tidigt ta ställning till om koncernrapporteringen ska bygga på:

• Konsolidering direkt från samtliga enskilda bolags transaktioner eller saldobalanser, eller

• En struktur där underkoncerner först konsolideras separat och därefter rapporteras vidare som redan konsoliderade enheter.

Detta är inte bara en teknisk fråga, utan påverkar spårbarhet, uppföljning, möjligheten till månadsvis konsolidering och den totala effektiviteten i processen.

**Öppna frågor med fokus på tillämpning**

En tydlig lärdom från de RFI-processer vi varit involverade i är att öppna frågor ger mest värde när de fokuserar på faktisk tillämpning. Frågor som rör hur arbetet genomförs i praktiken skapar ett helt annat samtal än frågor som enbart bekräftar att en funktion finns.

Exempel på sådana frågor är:

• Beskriv hur manuella respektive automatiska elimineringar hanteras i det löpande arbetet.

• Beskriv hur förändrade ägarandelar hanteras, till exempel när de förändras mitt under året.

• Beskriv hur spårbarhet mellan rapport, eliminering och underliggande data säkerställs.

**Beskriv nuläget – det formar svaren mer än man tror**

Något annat som har stor påverkan på svaren från leverantörerna är hur väl kommunen eller regionen beskriver sitt nuläge. I de RFI:er som lett till mest givande dialoger finns en tydlig bild av dagens arbetssätt och utmaningar.

**En begynnande trend: från tertial till månadsvis konsolidering**

I de senaste dialogerna ser vi en tydlig, om än fortfarande försiktig, trend. Allt fler kommuner uttrycker ett behov av att kunna konsolidera hela eller delar av koncernen oftare än tidigare. Där tertial länge varit norm efterfrågas nu i ökande utsträckning stöd för månadsvis konsolidering, åtminstone för vissa delar av kommunkoncernen.

**Inbjudan till frukostseminarium inför Ekonomichefsdagarna**

Dessa erfarenheter och reflektioner stod i centrum för det frukostseminarium som Tiego bjöd in till i samband med Ekonomichefsdagarna. Seminariet riktade sig till ekonomer i kommuner och regioner som står inför, eller nyligen har genomfört, en investering i koncernrapporteringssystem.

Datum: 15 januari 2026
Tid: 09:00–10:30
Plats: Hilton Stockholm Slussen, Guldgränd 8, Stockholm

Detta event har redan ägt rum. Har du frågor kring koncernrapportering för kommuner och regioner? Kontakta oss gärna.`,
  },
  {
    id: "25",
    title: "Tiego har erhållit ISAE 3402 typ 1",
    titleEn: "Tiego has obtained ISAE 3402 Type 1",
    excerpt:
      "Vi är glada att kunna meddela att Tiego har genomgått en oberoende granskning enligt ISAE 3402 typ 1, utförd av Forvis Mazars och Advisense.",
    excerptEn:
      "We are pleased to announce that Tiego has undergone an independent ISAE 3402 Type 1 examination, conducted by Forvis Mazars and Advisense.",
    categories: ["Tiego"],
    date: "22 dec 2025",
    image: "news/tiego-t.jpg",
    imageStyle: "contain",
    imageAlt: "Tiegos röda t-symbol",
    imageAltEn: "Tiego's red t symbol",
    
    images: {
      mazars: "news/forvis-mazars.png",
      advisense: "news/advisense.png",
    },
    imageAlts: {
      mazars: "Forvis Mazars logotyp",
      advisense: "Advisense logotyp",
    },
    imageAltsEn: {
      mazars: "Forvis Mazars logo",
      advisense: "Advisense logo",
    },
    content: `[img:mazars]

**ISAE 3402 typ 1 – en viktig milstolpe för våra kunder**

Vi är glada att kunna meddela att Tiego har genomgått en oberoende granskning enligt ISAE 3402 typ 1, utförd av Forvis Mazars och Advisense. Rapporten bekräftar att våra kontroller och processer, per den 30 november 2025, är ändamålsenligt utformade för att stödja våra kunders interna kontroll över finansiell rapportering.

För våra kunder – som använder Tiego Group Accounting och Tiego Lease Engine som centrala delar i sin koncernrapportering och leasingredovisning – innebär detta en ökad transparens och ett tydligt bevis på att våra rutiner och styrmodeller är utformade för att möta höga krav på tillförlitlighet, informationssäkerhet och regelefterlevnad.

[img:advisense]

**Vad innebär ISAE 3402 för dig som kund?**

ISAE 3402 är en internationellt erkänd standard för oberoende granskning av tjänsteleverantörers interna kontroller som är relevanta för kunders finansiella rapportering. En ISAE 3402 typ 1-rapport fokuserar på:

• Beskrivningen av Tiegos tjänster och system

• Fastställda kontrollmål kopplade till finansiell rapportering

• Utformningen av de kontroller som ska stödja kontrollmål och hantera relevanta risker

• Resultatet från den oberoende revisorns bedömning av implementerade kontroller

Rapporten är avsedd att användas av våra kunder och deras revisorer som underlag i samband med revision och intern kontroll.

**Ett strukturerat kontrollramverk baserat på ISO/IEC 27001**

ISAE 3402-granskningen bygger på Tiegos etablerade Information Security Management System (ISMS), som är uppbyggt i linje med ISO/IEC 27001:2022. ISMS:et utgör den gemensamma styrmodellen för riskhantering, informationssäkerhet, förändringshantering, incidenthantering, leverantörsstyrning och kontinuitetsplanering.

**Tillgänglighet för kunder och fortsatt utveckling**

ISAE 3402 typ 1-rapporten kommer att erbjudas till våra kunder mot en avgift och omfattar även tillgång till kommande ISAE 3402-rapporter. Detta är en del av vårt långsiktiga arbete med att göra Tiego till en ännu mer relevant och trygg leverantör för bolag med höga krav på intern kontroll och regelefterlevnad.

Arbetet med ISAE 3402 är också ett viktigt steg i vår fortsatta utveckling. Under kommande period fortsätter vi att stärka våra processer och kontroller, med målsättningen att genomföra en ISAE 3402 typ 2-granskning, som även omfattar kontrollernas funktion över tid.

För oss på Tiego är detta inte ett slutmål – utan en bekräftelse på att strukturerad styrning, transparens och kvalitet är en integrerad del av hur vi bygger och levererar våra tjänster.

Vill du veta mer om rapporten eller hur den kan användas i er revision? Kontakta oss gärna.`,
    contentEn: `[img:mazars]

**ISAE 3402 Type 1 – an important milestone for our customers**

We are pleased to announce that Tiego has undergone an independent ISAE 3402 Type 1 examination, conducted by Forvis Mazars and Advisense. The report confirms that, as of 30 November 2025, our controls and processes are suitably designed to support our customers' internal control over financial reporting.

For our customers—who use Tiego Group Accounting and Tiego Lease Engine as central components in their group reporting and lease accounting—this provides increased transparency and clear evidence that our procedures and governance models are designed to meet high standards of reliability, information security, and regulatory compliance.

[img:advisense]

**What does ISAE 3402 mean for you as a customer?**

ISAE 3402 is an internationally recognized standard for independent assurance engagements on service organizations' internal controls that are relevant to customers' financial reporting. An ISAE 3402 Type 1 report focuses on:

• The description of Tiego's services and systems

• Established control objectives related to financial reporting

• The design of controls intended to support the control objectives and manage relevant risks

• The results of the independent auditor's assessment of the implemented controls

The report is intended to be used by our customers and their auditors as supporting documentation in connection with audits and internal control activities.

**A structured control framework based on ISO/IEC 27001**

The ISAE 3402 examination is based on Tiego's established Information Security Management System (ISMS), which is designed in alignment with ISO/IEC 27001:2022. The ISMS constitutes the common governance framework for risk management, information security, change management, incident management, vendor management, and business continuity planning.

The examination covers, among other things, governance and management processes, risk management, access controls, change and release management, operational monitoring, incident management, backup and recovery, as well as business continuity and disaster recovery preparedness for our SaaS services.

**Availability for customers and continued development**

The ISAE 3402 Type 1 report will be made available to our customers for a fee and includes access to forthcoming ISAE 3402 reports. This forms part of our long-term commitment to making Tiego an even more relevant and reliable provider for organizations with high demands on internal control and regulatory compliance.

The ISAE 3402 initiative is also an important step in our continued development. In the coming period, we will continue to strengthen our processes and controls, with the objective of completing an ISAE 3402 Type 2 examination, which also covers the operating effectiveness of controls over time.

For us at Tiego, this is not an end goal—but a confirmation that structured governance, transparency, and quality are an integral part of how we build and deliver our services.

Would you like to know more about the report or how it can be used in your audit? Please feel free to contact us.`,
  },
  {
    id: "24",
    title: "Inbjudan: IFRS 18 förändrar spelplanen – är du redo?",
    excerpt:
      "Tillsammans med ProXecutive bjuder Tiego in till ett inspirerande frukostseminarium om hur IFRS 18 förändrar spelplanen och hur du förbereder dig.",
    categories: ["TGA", "IFRS 18"],
    date: "25 nov 2025",
    image: "news/ifrs18.jpg",
    imageStyle: "cover",
    imageAlt: "Anteckningsblock med texten IFRS 18 och diagram, bredvid en kaffekopp",
    imageAltEn: "Notebook with the text IFRS 18 and charts, next to a cup of coffee",
    
    content: `Tillsammans med ProXecutive bjuder vi på Tiego in till ett inspirerande frukostseminarium om hur IFRS 18 förändrar spelplanen och hur du förbereder dig. IFRS 18 ersätter IAS 1 och innebär en omfattande förändring av resultaträkningens struktur på över 20 år samt de mindre förändringarna i kassaflödet.

Under seminariet går vi igenom både bakgrunden och de praktiska konsekvenserna – med fokus på vad du behöver prioritera redan under 2025. Vi kombinerar expertgenomgångar med erfarenhetsutbyte – allt över en god frukost.

**Det här får du med dig:**

En pedagogisk genomgång av de nya kraven i IFRS 18

Praktiska exempel på och dialog om MPM, kassaflöden och kostnadsdisaggregering

Reflektioner kring systemstöd, kontoplaner och koncernrapportering

Rundabordssamtal med andra bolag – dela erfarenheter och skapa nya kontakter

**Datum:** 25 november
**Tid:** 08:30–10:00 (frukost från 08:00)
**Plats:** Sveavägen 68, Stockholm

Vi kommer att dela ett dokument med seminariets innehåll, och uppdatera det med insikter från rundabordssamtalen. Under 2026 planerar vi att bjuda in till ett uppföljande möte när fler kommit en bit på vägen.

Detta event har redan ägt rum. Håll utkik för kommande seminarier och uppföljande möten under 2026.

Varmt välkommen!
/Tiego & ProXecutive`,
  },
  {
    id: "23",
    title: "Användardagen 2025 – TGA 7.10",
    titleEn: "TGA 7.10",
    excerpt:
      "Bättre rapportflöde och starkare verktyg – TGA 7.10 i fokus på användardagen. Förmiddagen den 20 maj samlades över 30 användare av Tiego Group Accounting för en genomgång av version 7.10.",
    excerptEn:
      "Better reporting flow and stronger tools – TGA 7.10 in focus at the user day. On the morning of May 20, more than 30 users of Tiego Group Accounting gathered for a walkthrough of version 7.10.",
    categories: ["TGA"],
    date: "20 maj 2025",
    metaDescription: "Bättre rapportflöde och starkare verktyg: den 20 maj samlades över 30 användare av Tiego Group Accounting för en genomgång av version 7.10.",
    metaDescriptionEn: "Better reporting flow and stronger tools: on 20 May more than 30 users of Tiego Group Accounting gathered for a walkthrough of version 7.10.",
    image: "news/tga-icon.png",
    imageStyle: "contain",
    imageAlt: "Tiego Group Accounting-ikon, röd pyramid",
    imageAltEn: "Tiego Group Accounting icon, red pyramid",
    
    content: `**Bättre rapportflöde och starkare verktyg – TGA 7.10 i fokus på användardagen**

Förmiddagen den 20 maj samlades användare av Tiego Group Accounting (TGA) för en ordentlig genomgång av version 7.10. På plats fanns över 30 deltagare – både i rummet och via Teams – redo att få koll på nya funktioner, förbättringar och det som är på gång. Och precis som vanligt var dialogen minst lika viktig som själva demon.

**Stabil grund – testad i verkligheten**

Version 7.10 är långt ifrån en "prototyp". Den har redan varit igång i flera workshops, implementationer och pilotmiljöer sedan hösten 2024. Den 12 maj blev den officiellt släppt – lagom till att alla deltagare skulle få en full genomgång. Alla uppgraderingar sker i dialog med varje kund, så inget sker på chans.

Vi berättade också om hur vår utvecklingsprocess blivit mer strukturerad. Med tydliga roller, testplaner och kodgranskning från flera håll blir resultatet stabilare system – och snabbare hantering när något behöver justeras.

**Fokus på användarvänlighet – rapporteringen blir enklare**

För rapporterande användare märks flera nyheter direkt: snabbare laddning i "Hantera specifikationer", smartare filter, och förbättringar i EditSpec för att bättre hantera stora mängder data. Webben blir helt enkelt smidigare att arbeta i.

Checklistan i portalen har också fått ett lyft. Den hjälper er hålla koll på vilka bolag som är klara, vad som är kvar – och vad som är signerat. Fler har börjat använda den som "andra skärm" under rapporteringen, och responsen har varit väldigt positiv.

**Controllers och systemansvariga får fler verktyg**

En stor del av sessionen ägnades åt ny funktionalitet för controllers och SysAdmins. Bland annat visade vi:

Historikspårning: du ser inte bara aktuellt värde, utan även vad som gällde innan – och vem som ändrade det.

Nytt register för ägarandelar: gör det enklare att automatisera bokningar och ha kontroll över andelsförändringar.

Report Designer: elimineringar med formler, mallar och logik direkt i systemet.

Vi gick också igenom nya sexkolumnsrapporter, KPI:er direkt i databasen, och förbättrad valutahantering vid låsning av dotterbolag. Allt för att ge er mer kraft och kontroll i bokslutet – utan att det ska bli krångligare.

**Tydligare bokslut – med bättre koll på detaljerna**

En nyhet många uppskattade var kontrollfunktionen för tillgångar och skulder – som kompletterar den vi redan har för eget kapital. Den hjälper er fånga upp interna omklassificeringar som inte går jämnt ut, och ger ett bättre underlag till både kontroll och revision.

Vi visade även hur förvärvsdagar hanteras automatiskt vid justering – så att det manuella arbetet minskar, men med fortsatt koll under månaden då förändringen sker.

**Integration, datakvalitet och mer självservice**

TGA:s integrationer körs nu genom ett gemensamt ramverk – vilket ger bättre loggning, spårbarhet och support. Fler kunder väljer att använda våra "paketerade" lösningar som ger hög kontroll med låg starttröskel. Vi visade också nya självservicelösningar för att radera bolag, ändra bolagskoder och få bättre koll i valutahantering.

Datavalidering sker dessutom direkt vid inmatning, och felmeddelandena är tydligare – vilket minskar risken för irriterande stopp och ökar kvaliteten på det ni skickar in.

**Prognoser, hållbarhetsdata och det som är på gång**

Flera av våra användare bygger nu prognoser direkt i TGA, med hjälp av bättre verktyg för att kopiera utfall, styra perioder och uppdatera modeller. Vi har också förbättrat formuläret för operationell data – särskilt för de som rapporterar hållbarhets- eller volymuppgifter.

Mot slutet av dagen pratade vi också om framtiden: webbaserade dashboards, IFRS/K3-taggar i matriserna, delade dokumentytor via Notice Board och bättre onboarding av nya användare. Allt för att göra TGA ännu mer komplett – och samtidigt ännu enklare att använda.

Vill du testa något nytt i version 7.10 eller prata om kommande funktioner? Hör av dig till support@tiego.se – vi hjälper dig gärna komma igång!`,
    contentEn: `**Better Reporting Flow and Stronger Tools – TGA 7.10 in Focus at the User Day**

On the morning of May 20, users of Tiego Group Accounting (TGA) gathered for a thorough walkthrough of version 7.10. More than 30 participants joined – both in person and via Teams – ready to get up to speed on new features, enhancements, and what's coming next. And, as always, the dialogue was just as valuable as the demo itself.

**A Solid Foundation – Tested in the Real World**

Version 7.10 is far from a "prototype." It has already been used in several workshops, implementations, and pilot environments since fall 2024. On May 12, it was officially released – just in time for all participants to get the full walkthrough.

All upgrades are carried out in dialogue with each customer – nothing is left to chance.

We also shared how our development process has become more structured. With clearly defined roles, test plans, and code reviews from multiple angles, the result is a more stable system – and faster handling whenever adjustments are needed.

**Focus on User-Friendliness – Reporting Made Easier**

For reporting users, several new features are immediately noticeable: faster loading in "Manage Specifications," smarter filters, and improvements in EditSpec for better handling of large data volumes. The web experience is simply smoother – something we know many have requested.

The checklist in the portal has also been upgraded. It helps you keep track of which companies are done, what remains – and what's been signed off. More users are now using it as a "second screen" during reporting, and the feedback has been very positive.

**More Tools for Controllers and System Administrators**

A large part of the session was dedicated to new functionality for controllers and SysAdmins. We showcased, among other things:

History tracking: not only can you see the current value, but also what it was before – and who changed it.

New ownership register: simplifies automation of postings and provides better control over ownership changes.

Report Designer: eliminations using formulas, templates, and logic directly in the system.

We also covered new six-column reports, KPIs directly in the database, and improved currency handling when locking subsidiaries. All designed to give you more power and control in the closing process – without adding complexity.

**Clearer Financial Statements – Better Detail Control**

One of the most appreciated new features was the control function for assets and liabilities – complementing the one already in place for equity. It helps identify internal reclassifications that don't balance correctly and provides a stronger foundation for both control and audit.

We also demonstrated how acquisition dates are now handled automatically during adjustments – reducing manual work while maintaining full visibility during the month of change.

**Integration, Data Quality, and More Self-Service**

TGA's integrations now run through a unified framework – improving logging, traceability, and support. More customers are choosing our "packaged" integration solutions, which offer strong control with a low setup threshold.

We also demonstrated new self-service tools for deleting entities, changing company codes, and improving visibility in currency management.

Data validation now happens directly during input, and error messages are clearer – reducing annoying interruptions and improving the quality of submitted data.

**Forecasts, Sustainability Data, and What's Coming Next**

Several users are now building forecasts directly in TGA, using enhanced tools for copying outcomes, managing periods, and updating models. We have also improved the operational data form – especially for those reporting sustainability or volume data.

Toward the end of the day, we discussed what's ahead: web-based dashboards, IFRS/K3 tags in matrices, shared document spaces via Notice Board, and better onboarding for new users.

All designed to make TGA even more complete – and at the same time, even easier to use.

Want to try something new in version 7.10 or talk about upcoming features? Reach out to support@tiego.se – we'll be happy to help you get started!`,
  },
  {
    id: "26",
    title: "Frukostwebbinarium med Tiego & Ynnor – 5 februari",
    excerpt:
      "Skatteverkets ställningstagande om moms på leasing och vad det innebär i praktiken. Tiego och Ynnor bjöd in till ett gemensamt frukostwebbinarium.",
    categories: ["TLE"],
    date: "5 feb 2026",
    image: "news/ynnor-logo.jpg",
    imageStyle: "contain",
    imageAlt: "Ynnor logotyp",
    imageAltEn: "Ynnor logo",
    images: {
      ronny: "news/ronny-ynnor.jpg",
    },
    imageAlts: {
      ronny: "Föreläsare från Ynnor vid en laptop framför en projektorduk",
    },
    imageAltsEn: {
      ronny: "Presenter from Ynnor at a laptop in front of a projector screen",
    },
    content: `[img:ronny]

**Frukostwebbinarium – Skatteverkets ställningstagande om moms på leasing och vad det innebär i praktiken**

Skatteverkets ställningstagande om moms och finansiell leasing påverkar alla organisationer som leasar personbilar. För många innebär det att tidigare etablerade arbetssätt kring restvärden, förlängningar och utköp nu kan innebära en ökad risk för återkrav av moms och skattetillägg.

Den 5 februari bjöd Tiego och Ynnor in till ett gemensamt frukostwebbinarium där vi gick igenom vad ställningstagandet innebär i praktiken, vilka risker som kan uppstå – och hur man proaktivt kan analysera och strukturera sina leasingavtal framåt.

**Bakgrund – varför frågan är aktuell nu**

Skatteverkets ställningstagande om moms på finansiell leasing har under den senaste tiden fått ökad uppmärksamhet. I fokus står inte enskilda formuleringar i avtalen, utan hur leasingupplägg faktiskt fungerar i praktiken över tid. Detta gäller särskilt när det gäller restvärden, förlängningar och utköp.

Det är inte ovanligt att organisationer som leasar personbilar ännu inte har hunnit analysera hur ställningstagandet påverkar deras befintliga leasingportfölj. Samtidigt kan ett fortsatt arbete enligt tidigare mönster, utan analys, innebära en reell risk. I värsta fall kan leasingavtal som uppfattats som okomplicerade komma att ifrågasättas i efterhand, med konsekvenser i form av återkrav av avdragsgill moms och skattetillägg.

Det är denna förändrade riskbild, snarare än nya lagregler, som gör frågan högaktuell just nu.

**Etablerad praxis möter ny riskbedömning**

Många organisationer har under lång tid arbetat enligt affärsmässigt rationella och marknadsmässigt vedertagna upplägg. Det är exempelvis vanligt att leasingavtal innehåller villkor om att köpare ska anvisas av leasingtagaren vid kontraktets slut. Den anvisade köparen kan tex vara det egna bolaget, en extern auktionsfirma eller personal.

I det ursprungliga leasingavtalet kan restvärdet dessutom vara satt relativt lågt, just för att minimera risken för negativa ekonomiska effekter vid kontraktets slut. Ur ett affärsperspektiv har dessa upplägg ofta fungerat väl och skapat förutsägbarhet.

Mot bakgrund av Skatteverkets ställningstagande är det dock den samlade effekten av avtalsvillkor och faktisk tillämpning över tid som nu behöver analyseras. När låga restvärden kombineras med utköp som i praktiken framstår som självklara, kan ett leasingupplägg sett över hela sin livslängd komma att uppfattas som mer likt ett köp än en uthyrning.

**Bilpolicy – mer än ett dokument**

I många organisationer finns redan en bilpolicy som reglerar hur leasing, förlängningar och utköp ska hanteras. Mot bakgrund av det nya ställningstagandet blir det dock allt viktigare att bilpolicyn är tydlig och att den faktiskt följs i praktiken.

En central utgångspunkt är att lösen av bilar alltid bör ske via extern part, till marknadsmässiga villkor och att leasingtagaren inte själv, direkt eller indirekt, ska ha ett förutbestämt eller ekonomiskt gynnat utköp.

Samtidigt räcker det inte att policyn är korrekt formulerad. Minst lika viktigt är att följa upp hur lösen och utköp faktiskt har genomförts historiskt, analysera om tillämpningen har varit konsekvent över tid och identifiera eventuella avvikelser mellan policy och faktisk hantering.

**Vad bör man göra nu?**

I frukostwebbinariet gav vi deltagarna konkreta och praktiska förslag på åtgärder:

1. Analysera kontraktstexterna – Gå igenom hur restvärden, förlängningar och villkor om anvisad köpare är formulerade i leasingavtalen.

2. Se över bilpolicyn och faktisk tillämpning – Säkerställ att bilpolicyn tydligt anger att lösen ska ske via extern part och följ upp hur lösen och utköp faktiskt har genomförts historiskt.

3. Genomföra en effekt- och riskanalys – Bedöm den potentiella risken avseende återkrav av moms och eventuella skattetillägg, baserat på befintliga leasingavtal och historisk hantering.

Denna genomgång bör göras proaktivt, innan frågorna ställs av revisorer eller Skatteverket – antingen internt eller med stöd av specialister, exempelvis Ynnor.

För organisationer med en större leasingflotta kan även eget leasingbolag vara ett relevant alternativ. När volymerna är tillräckliga kan detta utgöra ett tydligt business case genom bättre kontroll över avtal, restvärden och uppföljning.

**När analys möter systemstöd – från Tiegos perspektiv**

Avslutningsvis visade Tiego hur dessa frågor ser ut i praktiken när leasingdata analyseras på kontraktsnivå. Tiego arbetar med kunder som har allt från ett fåtal leasingkontrakt till tusentals, ofta med månatlig import av data direkt från leasinggivare.

I webbinariet visade Kevin Hall kort hur Tiego effektiviserar leasingredovisning enligt K3, IFRS 16 och RKR R5. Tiegos system säkerställer korrekt och spårbar koncernrapportering och gör att notupplysningarna till årsredovisningen kan tas fram tidigt.

Detta event har redan ägt rum. Har du frågor kring Skatteverkets ställningstagande och hur det påverkar er leasingportfölj? Kontakta oss gärna.`,
  },
  {
    id: "22",
    title: "Användardagen 2025 – TLE 7.10",
    titleEn: "TLE 7.10",
    excerpt:
      "Mindre manuell hantering, mer insyn och full spårbarhet. Den 12 maj samlades användare av Tiego Lease Engine för att upptäcka vad som är nytt i version 7.10.",
    excerptEn: "Less manual effort, more insight, and full traceability. On May 12, users of Tiego Lease Engine gathered to explore what's new in version 7.10.",
    categories: ["TLE"],
    date: "12 maj 2025",
    metaDescription: "Mindre manuell hantering, mer insyn och full spårbarhet. Den 12 maj samlades användare av Tiego Lease Engine för att se vad som är nytt i version 7.10.",
    metaDescriptionEn: "Less manual effort, more insight and full traceability. On 12 May users of Tiego Lease Engine gathered to explore what's new in version 7.10.",
    image: "news/tle-icon.png",
    imageStyle: "contain",
    imageAlt: "Tiego Lease Engine-ikon, turkos pyramid",
    imageAltEn: "Tiego Lease Engine icon, teal pyramid",
    
    content: `**TLE 7.10 – Nya algoritmer, starkare kontroll och enklare leasingredovisning**

Mindre manuell hantering, mer insyn och full spårbarhet. Den 12 maj (och 13 maj för engelsktalande kunder) samlades användare av Tiego Lease Engine (TLE) för att upptäcka vad som är nytt i version 7.10 – och varför leasingredovisning nu kan bli både enklare och mer korrekt.

Tiego Lease Engine är Tiegos specialistverktyg för IFRS 16, K3 och kommunala regelverk som RKR R5. Det används för att förenkla och säkra hanteringen av leasingkontrakt – från första registrering till sista bokning. På användardagen visades ny funktionalitet som bygger på verkliga behov, testats i skarpa miljöer och redan börjat ge effekt i bokslutsarbetet.

Kevin Hall, produktansvarig för TLE: "Vi bygger TLE tillsammans med våra kunder – och 7.10 visar vad som händer när vi lyssnar, testar och förbättrar i cykler. Det handlar inte om fler klick – utan om färre fel, bättre dokumentation och mindre stress."

**Från leasinggivare till färdig bokning – med ett klick mindre**

En av de mest uppskattade nyheterna är den förbättrade importfunktionen. Användare kan nu läsa in förändringar direkt från leasinggivare eller försystem – i Excel eller andra format – och få hjälp att matcha, granska och godkänna. Det minskar dubbelarbete och risken för felregistrering.

"Det här är en funktion som sparar timmar – särskilt för större bolag eller de som gör många kontraktsändringar", berättade en deltagare efter användardagen.

**Ny räntealgoritm – mer rättvisande kassaflöde**

I version 7.10 har modellen för ränteberäkning förändrats. Tidigare bokades räntan som en förutbetald kostnad – nu hanteras den i efterskott, i takt med att räntan faktiskt förbrukas. Resultateffekten blir densamma, men kassaflödet speglar verkligheten bättre.

För bolag med sena förändringar eller många retroaktiva justeringar innebär det mer precision – och enklare dialog med revisorerna.

**Full kontroll över tillgångens livscykel**

Med nya parametrar får användare bättre styrning över när avskrivningar ska börja och sluta, hur utrangering ska ske och vilket restvärde som ska kvarstå. Dessutom hanteras ändrade månadskostnader mitt i perioden på ett nytt sätt – med mindre resultateffekt och tydligare logik.

Mattias Elfgren, VD på Tiego: "Vi vill göra leasingredovisning lika robust som annan redovisning. Det ska gå att lita på varje värde – och förstå varje siffra."

**Byggt för kommuner – och alla som jobbar med RKR R5**

En annan viktig nyhet gäller stöd för implicit ränta enligt RKR R5. TLE hanterar nu automatiskt beräkningar där marknadsvärde, restvärde och löptid styr räntan – och dokumenterar hela processen. Det gör det lättare för kommuner och regioner att följa regelverket, och underlättar vid granskning och revision.

**Dokumentation som verktyg – inte bara bilaga**

TLE har nu en komplett systembeskrivning – framtagen för att möta revisorers krav, men också fungera som uppslagsverk för controllers och redovisningsekonomer. Där beskrivs bland annat hur systemet hanterar hyresfria månader, retroaktiva kontrakt och diskonteringsränta vid förlängningar – med full spårbarhet från kontrakt till bokföring.

**Vad är nästa steg?**

Utvecklingen fortsätter. Kommande månader fokuserar Tiego på att bygga fler övningsexempel in i systembeskrivningen, stärka valideringslogik och automatiserade kontroller, höja säkerheten enligt ISO 27001 och förbereda TLE för revision enligt ISAE 3402.

Samtidigt pågår arbetet med nästa version, där bland annat nya rapportvyer, attestfunktioner och förbättrad drilldown är på gång.

Vill du veta mer om hur Tiego Lease Engine kan effektivisera och säkra din leasingredovisning? Hör av dig till support@tiego.se – eller boka en demo direkt.`,

    contentEn: `**TLE 7.10: New Algorithms, Greater Transparency, and Smoother Workflows**

Less manual effort, more insight, and full traceability. On May 12 (and May 13 for English-speaking customers), users of Tiego Lease Engine (TLE) gathered to explore what's new in version 7.10 – and how lease accounting is now both simpler and more accurate.

Tiego Lease Engine is Tiego's specialist tool for IFRS 16, K3, and municipal regulations such as RKR R5. It's designed to simplify and secure the management of lease contracts – from initial registration to final posting. During the user day, new features were showcased that are based on real customer needs, tested in live environments, and already showing results in closing processes.

Kevin Hall, Product Manager for TLE: "We build TLE together with our customers – and 7.10 is the result of listening, testing, and improving in cycles. It's not about more clicks – it's about fewer errors, better documentation, and less stress."

**From Lessor to Final Posting – With One Less Click**

One of the most appreciated updates is the improved import function. Users can now upload changes directly from lessors or upstream systems – in Excel or other formats – and get assistance to match, review, and approve. This reduces double work and the risk of incorrect entries.

"This feature saves hours – especially for larger companies or those managing many contract changes," one attendee shared after the event.

**New Interest Algorithm – More Accurate Cash Flow**

Version 7.10 introduces a new interest calculation model. Previously, interest was booked as a prepaid cost – now it is recognized in arrears, aligned with actual usage. The effect on profit remains the same, but the cash flow presentation is more realistic.

For companies with late changes or multiple retroactive adjustments, this means greater precision and easier discussions with auditors.

**Full Control Over Asset Lifecycle**

With new parameters, users gain better control over when depreciation starts and ends, how disposals are handled, and what residual value remains. Additionally, mid-period cost changes are now processed in a way that reduces impact on the income statement and improves clarity.

Mattias Elfgren, CEO at Tiego: "We want lease accounting to be as robust as any other reporting. You should be able to trust every value – and understand every number."

**Built for Municipalities – and Everyone Working with RKR R5**

Another key update is support for implicit interest rates under RKR R5. TLE now automatically calculates rates based on market value, residual value, and duration – and documents the entire process. This helps municipalities and regions comply with the rules and simplifies audits.

**Documentation as a Tool – Not Just an Attachment**

TLE now includes a complete system description – developed to meet auditor requirements but also serving as a reference for controllers and accountants. It describes how the system handles rent-free periods, retroactive contracts, and discount rates during extensions – with full traceability from contract to ledger.

**What's Next?**

Development continues. In the coming months, Tiego will focus on:

- adding more practice examples to the system documentation,
- enhancing validation logic and automated checks,
- increasing security in line with ISO 27001,
- and preparing TLE for ISAE 3402 audit readiness.

Meanwhile, work is underway on the next version, which will include new report views, approval workflows, and improved drill-down capabilities.

Want to learn more about how Tiego Lease Engine can streamline and safeguard your lease accounting? Contact support@tiego.se – or book a demo today.`,
  },
  {
    id: "28",
    title: "Koncernredovisningssystem och räkenskapsinformation",
    titleEn: "Group Accounting Systems and Financial Reporting Information",
    excerpt:
      "Den 28 augusti bjuder KEF in till en spännande och informativ dag med fokus på kommunernas behov av ett effektivt koncernredovisningssystem.",
    excerptEn: "On August 28, KEF invites you to an exciting and informative day focused on the needs of municipalities for an effective group accounting system.",
    categories: ["TGA"],
    date: "26 mar 2025",
    metaDescription: "Den 28 augusti bjuder KEF in till en dag med fokus på kommunernas behov av ett effektivt koncernredovisningssystem. Tiego medverkar.",
    metaDescriptionEn: "On 28 August KEF invites you to a day focused on municipalities' need for an effective group accounting system. Tiego takes part.",
    image: "news/kef-logo.png",
    imageStyle: "contain",
    imageAlt: "Kommunalekonomernas förening (KEF) logotyp",
    imageAltEn: "Association of Municipal Economists (KEF) logo",
    content: `Den 28 augusti bjuder KEF in till en spännande och informativ dag med fokus på kommunernas behov av ett effektivt koncernredovisningssystem. Under dagen kommer KEF att belysa hur koncernbokslut och koncernrapportering i kommunala bolag kan förbättras med hjälp av programvara, och vi kommer att ge en överblick av de mest relevanta systemen på marknaden.

Läs mer här: https://www.kef.se/utbildningar/koncernredovisningssystem-och-rakenskapsinformation`,
    contentEn: `On August 28, KEF invites you to an exciting and informative day focused on the needs of municipalities for an effective group accounting system. During the day, KEF will highlight how consolidated financial statements and group reporting in municipal companies can be improved with the help of software, and we will provide an overview of the most relevant systems on the market.

Read more here: https://www.kef.se/utbildningar/koncernredovisningssystem-och-rakenskapsinformation`,
  },
  {
    id: "21",
    title: "5 år efter införandet av IFRS 16: så funkar \"nya\" leasingstandarden",
    titleEn: "5 Years After the Implementation of IFRS 16: How the \"New\" Leasing Standard Works",
    excerpt:
      "Kevin Hall från Tiego och Michael Thorstensson från Deloitte delar med sig av lärdomar och erfarenheter sedan införandet av IFRS 16 Leasing till FAR.",
    excerptEn: "Kevin Hall from Tiego and Michael Thorstensson from Deloitte share insights and experiences since the implementation of IFRS 16 Leasing with FAR.",
    categories: ["TLE", "IFRS 16"],
    date: "18 dec 2024",
    image: "news/kevin-mic.jpg",
    imageStyle: "portrait",
    imageAlt: "Kevin Hall, Tiego, talar i mikrofon i studiomiljö",
    imageAltEn: "Kevin Hall, Tiego, speaking into a microphone in a studio setting",
    content: `För några år sedan ändrades reglerna kring hur företag ska redovisa sina leasingavtal. Kevin Hall från Tiego och Michael Thorstensson från Deloitte delar med sig av lärdomar och erfarenheter sedan införandet av IFRS 16 Leasing.

Klädföretag som hyr sina butikslokaler, byggbolag som hyr grävmaskiner eller företag inom logistikbranschen som hyr flygplan eller lastbilar. Exemplen på så kallad leasing kan göras lång och handla om allt från småskaliga verksamheter till stora börsbolag.

Sedan 2019 gäller en ny standard inom redovisning av leasingavtal. IFRS 16 Leasing, som ersatte den tidigare standarden IAS 17, gäller för alla koncerner som använder IFRS (International Financial Reporting Standards) som regelverk i sin redovisning.

Läs mer om Kevin reflektioner här: https://www.far.se/ekonomisverige/finansiell-rapportering/5-ar-efter-inforandet-av-ifrs-16-sa-funkar-nya-leasingstandarden/

**Vad innebär och vad var syftet med IFRS 16 Leasing?**

Michael: – IFRS 16 Leasing innebär en enhetlig redovisning för alla avtal som uppfyller standardens definition av leasingavtal. Till skillnad från K3-regelverket så görs det inom IFRS 16 alltså ingen gränsdragning mellan finansiella och operationella leasingavtal för leasetagaren. Det innebär att man i princip måste redovisa alla sina leasingavtal i balansräkningen som en tillgång och en skuld.

**Vilka behöver tillämpa IFRS 16 Leasing i Sverige?**

Kevin: – Det gäller för alla bolag som använder IFRS-regelverket – det vill säga alla börsbolag i Sverige, men också onoterade bolag, ofta med global verksamhet och utländska investerare, som själva valt att använda modellen.

**Vad finns det för utmaningar med den nuvarande standarden?**

Michael: – Den är tekniskt komplex för användarna. Standarden innebär också att man behöver mer tillgång till information och att fler personer behöver involveras. En annan utmaning är att standarden på många sätt är utformad enligt ett synsätt som är baserat på det enskilda kontraktet.

**Hur ser kunskapsläget ut hos företag som använder standarden?**

Michael: – De allra flesta läser på och är verkligen måna om att det ska bli rätt. Men för en del blir det system man använder för rapportering lite av en "black box". Det gäller att förstå regelverket och indatan också.

Kevin: – Jag upplever att många läste på mycket runt 2018 inför införandet, men att man inte är riktigt lika på tårna eller uppdaterade idag. Men vi hjälper kunder att läsa regelverket, ge information om vilka data som krävs och vad man ska lägga in för att systemet ska göra det man önskar.

**Lärdomar fem år efter införandet**

Kevin: – En lärdom är att många kunder var ganska sent ute och underskattade hur lång tid det skulle ta för organisationen att sätta in sig i ett nytt regelverk.

Michael: – Att företagen fått större förpliktelser och mer informationskrav har också varit positivt och gynnat den interna uppföljningen av verksamheten. För multinationella företag har det exempelvis gjort det möjligt att få bättre kontroll och överblick över leasingaktiviteten i ett dotterbolag.`,
    contentEn: `A few years ago, the rules regarding how companies should report their lease agreements changed. Kevin Hall from Tiego and Michael Thorstensson from Deloitte share insights and experiences since the implementation of IFRS 16 Leasing with FAR.

Clothing companies renting their store premises, construction companies leasing excavators, or logistics companies renting airplanes or trucks—examples of leasing abound and span everything from small-scale businesses to large publicly traded corporations.

Since 2019, a new standard for accounting lease agreements has been in effect. IFRS 16 Leasing, which replaced the previous standard IAS 17, applies to all groups using IFRS (International Financial Reporting Standards) as their accounting framework.

Read more about Kevin's reflections here: https://www.far.se/ekonomisverige/finansiell-rapportering/5-ar-efter-inforandet-av-ifrs-16-sa-funkar-nya-leasingstandarden/

**What does IFRS 16 Leasing mean and what was its purpose?**

Michael: – IFRS 16 Leasing means a uniform accounting treatment for all agreements that meet the standard's definition of a lease agreement. Unlike the K3 framework, IFRS 16 makes no distinction between financial and operating leases for the lessee. This means that virtually all lease agreements must be recognized on the balance sheet as an asset and a liability.

**Who needs to apply IFRS 16 Leasing in Sweden?**

Kevin: – It applies to all companies using the IFRS framework—that is, all listed companies in Sweden, but also unlisted companies, often with global operations and foreign investors, that have chosen to use the model themselves.

**What challenges exist with the current standard?**

Michael: – It is technically complex for users. The standard also requires greater access to information and more people to be involved. Another challenge is that the standard is in many ways designed from a perspective based on the individual contract.

**What is the level of knowledge among companies using the standard?**

Michael: – The vast majority study it carefully and are truly committed to getting it right. But for some, the system they use for reporting becomes a bit of a "black box." It's important to understand the framework and the input data as well.

Kevin: – I feel that many studied extensively around 2018 ahead of the implementation, but are not quite as sharp or up to date today. But we help customers read the regulations, provide information about what data is required and what to enter so the system does what they want.

**Lessons learned five years after implementation**

Kevin: – One lesson is that many customers were quite late and underestimated how long it would take for the organization to familiarize itself with a new regulatory framework.

Michael: – The fact that companies have taken on greater obligations and more information requirements has also been positive and benefited the internal monitoring of operations. For multinational companies, for example, it has made it possible to gain better control and oversight of leasing activity in a subsidiary.`,
  },
  {
    id: "21b",
    title: "Så förenklar du fusionsprocessen",
    titleEn: "How to Simplify the Merger Process",
    excerpt:
      "Fusioner är mer komplexa än de kan verka vid första anblick. Experterna Erik Mellgren och Mattias Elfgren betonar att grundlig förberedelse och uppmärksamhet på detaljer är avgörande för en smidig fusionsprocess.",
    excerptEn: "Mergers are more complex than they might seem at first glance. Experts Erik Mellgren and Mattias Elfgren emphasize that thorough preparation and attention to detail are crucial for a smooth merger process.",
    categories: ["TGA"],
    date: "18 dec 2024",
    metaDescription: "Fusioner är mer komplexa än de verkar. Erik Mellgren och Mattias Elfgren om varför grundlig förberedelse och noggrannhet avgör en smidig fusionsprocess.",
    metaDescriptionEn: "Mergers are more complex than they seem. Erik Mellgren and Mattias Elfgren on why thorough preparation and attention to detail ensure a smooth merger.",
    image: "news/ceo-presenting.png",
    imageStyle: "cover",
    imageAlt: "Mattias Elfgren presenterar vid ett ståbord med mikrofon under en filminspelning",
    imageAltEn: "Mattias Elfgren presenting at a standing table with a microphone during a video recording",
    content: `Fusioner är mer komplexa än de kan verka vid första anblick. Experterna Erik Mellgren och Mattias Elfgren på Tiego betonar att grundlig förberedelse och uppmärksamhet på detaljer är avgörande för en smidig fusionsprocess.

– I grund och botten handlar en fusion om att flytta siffror från ett bolag till ett annat, framgången ligger i att ha kontroll på detaljerna och en tydlig struktur för genomförandet, säger Erik Mellgren, koncernredovisningskonsult på Tiego.

Läs mer om Erik och Mattias reflektioner på FAR: https://www.far.se/ekonomisverige/finansiell-rapportering/sa-gor-du-fusionsprocessen-smidigare/

Det finns olika typer av fusioner, där en uppströmsfusion är den vanligaste. Det innebär att ett dotterbolags samtliga tillgångar, skulder och eget kapital tas över av moderbolaget. Samtidigt upplöses dotterbolaget utan likvidation.

Mattias Elfgren, vd på Tiego, tillägger att en tydlig struktur i kontohanteringen kan spara mycket tid. – Var inte heller rädd för att skapa nya konton vid behov, om man klumpar ihop saldon på gemensamma konton kan det försvåra senare avstämningar och spårbarhet.

– Något annat som är viktigt att ha koll på är koncerninterna skulder och fordringar. Där bör man gärna reglera dem innan fusionsdagen. Det är viktigt att komma ihåg att det fusionerade bolaget faktiskt upphör att existera på själva fusionsdagen.

**Fyra tips inför en företagsfusion**

1. Läs Bokföringsnämndens handledning och exemplen där.

2. Reglera och stäm av alla mellanhavanden mellan bolagen före fusionsdagen. Se till att alla parter är överens om beloppen.

3. Skapa en detaljerad kontoplan med matchning ett till ett mellan bolagen. Våga skapa nya konton i stället för att klumpa ihop saldon.

4. Dokumentera processen så att det finns checklistor och körscheman att återanvända nästa gång.`,

    contentEn: `Mergers are more complex than they might seem at first glance. Experts Erik Mellgren and Mattias Elfgren emphasize that thorough preparation and attention to detail are crucial for a smooth merger process.

– Essentially, a merger is about transferring numbers from one company to another. Success lies in having control over the details and a clear structure for execution, says Erik Mellgren, corporate accounting consultant at Tiego.

Read more about Erik and Mattias' reflections on FAR: https://www.far.se/kunskap/redovisning/sa-gor-du-fusionsprocessen-smidigare/

There are different types of mergers, where an upstream merger is the most common. This means that a subsidiary's entire assets, liabilities, and equity are taken over by the parent company. At the same time, the subsidiary is dissolved without liquidation.

Mattias Elfgren, CEO at Tiego, adds that a clear structure in account management can save a lot of time. – Don't be afraid to create new accounts when needed; if you lump balances together on shared accounts, it can complicate later reconciliations and traceability.

– Another important thing to keep track of is intercompany debts and receivables. These should preferably be settled before the merger date. It's important to remember that the merged company actually ceases to exist on the merger date itself.

**Four Tips for a Company Merger**

1. Read the Swedish Accounting Standards Board's guidance and the examples provided.

2. Settle and reconcile all intercompany transactions between the companies before the merger date. Make sure all parties agree on the amounts.

3. Create a detailed chart of accounts with one-to-one matching between the companies. Don't be afraid to create new accounts rather than lumping balances together.

4. Document the process so that there are checklists and run sheets to reuse next time.`,
  },
  {
    id: "20",
    title: "Viktiga råd inför er upphandling av koncernrapporteringssystem",
    titleEn: "Important Advice for Your Procurement of Group Reporting Systems",
    excerpt:
      "Tiego har delat med sig av erfarenheter från att besvara anbud i öppna förfaranden till kommuner och regioner på webbinarium anordnade av Kommunalekonomernas förening (KEF).",
    excerptEn: "Tiego has shared its experiences of responding to tenders in open procedures for municipalities and regions in webinars organized by the Association of Municipal Economists (KEF).",
    categories: ["TGA"],
    date: "4 dec 2024",
    heading: "Råd inför upphandling av koncernrapporteringssystem",
    headingEn: "Advice for Procuring Group Reporting Systems",
    metaDescription: "Tiego delar erfarenheter från att besvara anbud i öppna förfaranden till kommuner och regioner, från webbinarier anordnade av KEF.",
    metaDescriptionEn: "Tiego shares its experience of responding to tenders in open procedures for municipalities and regions, from webinars organized by KEF.",
    image: "news/magnus-josephson.jpg",
    imageStyle: "portrait",
    imageAlt: "Porträtt av Magnus Josephson, upphandlingskonsult",
    imageAltEn: "Portrait of Magnus Josephson, procurement consultant",
    images: {
      magnus: "news/magnus-josephson.jpg",
      mattias: "news/mattias-vit2.png",
    },
    imageAlts: {
      magnus: "Porträtt av Magnus Josephson, upphandlingskonsult",
      mattias: "Porträtt av Mattias Elfgren, VD på Tiego",
    },
    imageAltsEn: {
      magnus: "Portrait of Magnus Josephson, procurement consultant",
      mattias: "Portrait of Mattias Elfgren, CEO of Tiego",
    },
    content: `Tiego har delat med sig av erfarenheter från att besvara anbud i öppna förfaranden till kommuner och regioner på webbinarium anordnade av Kommunalekonomernas förening (KEF). Vi har uppmärksammat att det finns ett antal utvecklingstendenser inom kommunal redovisning som påskyndar behovet av koncernredovisningssystem. Ett exempel på detta är att Rådet för kommunal redovisning har skärpt texten om att det är ökade krav att konsolidera även då ägarandel är relativt låg. Även för den kommunala räkenskapsrevision är kraven skärpta då granskning ska göras på reviderade räkenskaper och därmed ökar tidspressen. Sist men inte minst så bildas allt större värden i koncernen varför en bra och snabb redovisning gagnar politiken och beslutsfattare.

[img:magnus]

**Del 1: Allmänna rekommendationer**

Tiegos upphandlingskonsult Magnus Josephson presenterade sina erfarenheter från tusentals upphandlingar men anpassat till upphandling av koncernrapporteringssystem i nedanstående nio punkter. Magnus Josephson har erfarenhet från båda leverantörens perspektiv och kommuner/regioner.

1. Definition av "System" – Att köpa ett "system" innebär underförstått att det är en kombination av produkter och tjänster som ska användas under en längre tidsperiod, minst 8-10 år.

2. Skilj på "Köp" och "Investering" – Det är vanligt vid investering i system att det läggs mer fokus på resultatet av upphandlingen (projektmål) än på resultatet av investeringen (effektmål/mätbara resultat över hela avtalsperioden).

3. Bestäm mätbara "Mål" för investeringen – Vilka mätbara mål finns att uppnå med ett koncernredovisningssystem som ger maximal nytta?

4. Utvärdera anbudsgivarens förmåga att uppnå målen – Den leverantör som kan visa bäst fakta, dvs exempel och bevis på att de kommer att nå målen är den som ska få högst poäng.

5. Vikta priset max 20% resterande 80% gäller förutsättningarna för att nå målen.

6. Varför bör förhandlat förfarande övervägas istället för öppet?

7. Rätt team – Att upphandla är ett lagarbete. Huvudansvarig ska vara verksamhetschefen som har kunskap om hur man ska använda system.

8. Avtalet ska börja med syftet med samarbetet – detaljerna löser man under avtalets gång.

9. Ha löpande uppföljning av mätetal av kvalitetsfaktorer.

[img:mattias]

**Del 2: Tiegos erfarenheter från aktuella förfrågningsunderlag**

1. Utvärderingsmodell – Tiego har sett väl genomtänkta och ambitiösa utvärderingsmodeller och det behöver vara kvalificerande krav på leverantörer.

2. Införandeprojektet – Tiego uppskattar kravet att kräva att en införandeplan ska bifogas anbudet.

3. Bilaga köparens nuläge och förutsättningar – Beskriv nuläget så noggrant som möjligt inklusive antal personer involverade och tid för bokslutsprocessen.

4. Funktionskrav – Koncernkonsolideringsmetoder, koncernstruktur, kontostruktur, koncerninterna poster, elimineringar, flödesanalyser, noter, kassaflöde, effekt och rapporter.

5. Svarsbilaga / Offert – Tiegos erfarenhet är att det finns en risk att ett förutbestämt sätt att beräkna pris ger kommunen en icke optimal prismodell.`,
    contentEn: `Tiego has shared its experiences of responding to tenders in open procedures for municipalities and regions in webinars organized by the Association of Municipal Economists (KEF). We have noticed several development trends in municipal accounting that accelerate the need for group reporting systems. One example is that the Council for Municipal Accounting has tightened the language regarding increased requirements to consolidate even when ownership stakes are relatively low. The requirements for municipal accounting audits have also been tightened, as reviews must be conducted on audited financial statements, which increases time pressure. Last but not least, increasing values are being generated within the group, making efficient and timely reporting beneficial to politics and decision-makers.

[img:magnus]

**Part 1: General Recommendations**

Tiego's procurement consultant Magnus Josephson presented his experiences from thousands of procurements, specifically adapted to the procurement of group reporting systems, in the following nine points. Magnus Josephson has experience from both the supplier and municipal/regional perspectives.

1. Definition of "System" – Purchasing a "system" implicitly means a combination of products and services that will be used over a long period, at least 8-10 years. You need to have a long-term perspective.

2. Distinguish between "Purchase" and "Investment" – It is common when investing in a system to focus more on the result of the procurement (project goals) rather than on the result of the investment (effect goals/measurable outcomes throughout the contract period).

3. Define Measurable "Goals" for the Investment – What measurable goals can be achieved with a group reporting system that provides maximum benefit?

4. Evaluate the Tenderer's Ability to Achieve the Goals – The supplier that can provide the best evidence, i.e., examples and proof that they will achieve the goals, should receive the highest score.

5. Weight the Price a Maximum of 20%, with the Remaining 80% for the Conditions to Achieve the Goals.

6. Why Should Negotiated Procedures Be Considered Instead of Open Procedures? – A negotiated procedure can be used when it is difficult to foresee what the optimal solution will be without negotiating with the supplier.

7. The Right Team – Procurement is a team effort. The main responsibility should lie with the business manager who has knowledge of how the system should be used.

8. The Agreement Should Start with the Purpose of the Collaboration – Details will be worked out during the contract period.

9. Ongoing Monitoring of Key Quality Metrics – Metrics should reflect that both the supplier and buyer have done things on time, with the correct figures, at the agreed price, and fulfilled the contract.

[img:mattias]

**Part 2: Tiego's Experiences from Current Tender Documents**

Evaluation Model – Tiego has seen well-thought-out and ambitious evaluation models, and there need to be qualifying requirements for suppliers. It is more beneficial for suppliers if the buyer asks open questions with descriptive requirements rather than using detailed must-have requirements.

Implementation Project – Tiego appreciates the requirement to submit an implementation plan as part of the tender. Tiego's implementation plans always include milestones where the status is evaluated during the implementation in three categories: 1. Registers and reports, 2. Input data, 3. Consolidation. Regarding training, Tiego believes that participants' system competencies should be built over time, and it is recommended to document what Tiego calls a "test level" at each milestone.

Annex: Buyer's Current Situation and Conditions – Describe the current situation as accurately as possible, including the number of people involved and the time for the closing process. Common reasons for deciding to invest in a group reporting system include time-consuming processes, reliance on specific individuals, high complexity with many companies, and the need for monthly financial reports.

Functional Requirements – In an open procedure, many qualifying MUST requirements increase the risk of disqualification and lead to fewer suppliers responding to the tender. Key areas include: Group Consolidation Methods, Group Structure, Account Structure, Intercompany Transactions, Eliminations, Flow Analysis, Notes, Cash Flow, and Impact and Reports.

Response Appendix / Offer – Tiego's experience is that there is a risk that a predetermined way of calculating prices leads to a suboptimal pricing model for the municipality. A negotiated procedure provides both the supplier and the buyer with a better starting point to create cost efficiency and added value.`,
  },
  {
    id: "20b",
    title: "Implementera RKR R5: FEM steg för framgångsrik leasingredovisning i kommuner",
    titleEn: "Implementing RKR R5: 5 Steps for Successful Lease Accounting in Municipalities",
    excerpt:
      "Tiego har genomfört ett webbinarium om erfarenheter från implementation av RKR R5 som Kommunalekonomernas förening (KEF) anordnat.",
    excerptEn: "Tiego has conducted a webinar on experiences from the implementation of RKR R5, organized by the Swedish Association of Municipal Economists (KEF).",
    categories: ["TLE"],
    date: "4 dec 2024",
    heading: "Implementera RKR R5 i fem steg",
    headingEn: "Implementing RKR R5 in Five Steps",
    metaDescription: "Tiego har genomfört ett webbinarium för KEF om erfarenheter från implementation av RKR R5 – fem steg för framgångsrik leasingredovisning i kommuner.",
    metaDescriptionEn: "Tiego held a webinar for KEF on experiences from implementing RKR R5 – five steps for successful lease accounting in municipalities.",
    image: "news/tiego-employee-98.jpg",
    imageStyle: "portrait",
    imageAlt: "Porträtt av en medarbetare på Tiego",
    imageAltEn: "Portrait of a Tiego employee",
    images: {
      mattias: "news/mattias-vit2-2.png",
      thomas: "news/tiego-employee-98.jpg",
    },
    imageAlts: {
      mattias: "Porträtt av Mattias Elfgren, VD på Tiego",
      thomas: "Porträtt av en medarbetare på Tiego",
    },
    imageAltsEn: {
      mattias: "Portrait of Mattias Elfgren, CEO of Tiego",
      thomas: "Portrait of a Tiego employee",
    },
    content: `Tiego har genomfört ett webbinarium om erfarenheter från implementation av RKR R5 som Kommunalekonomernas förening (KEF) anordnat. Rådet för kommunal redovisning (RKR) har sedan många år tagit fram rekommendationer om redovisning av leasing vilket ledde fram till publiceringen av rekommendation R5 i maj 2023 som följdes upp av idéskriften "Redovisning av leasing- och hyresavtal" i november 2023.

En enkät hade skickats ut inför utbildningen som 14 kommuner / regioner svarade på. Några av frågorna och svaren redovisas nedan.

[img:mattias]

**Del 1: Bakgrund och regelverk**

Fråga A: Hur väl uppfyllde 2023 års årsredovisning den uppdaterade RKR R5?

Svar A: Snitt 2,1 med sex stycken som svarade "Inte alls".

Syftet med RKR R5 är att skapa en neutral och jämförbar redovisning, oavsett hur kommuner och regioner väljer att finansiera resursanskaffningen. När RKR skrev in vad vi benämner som "80%-regeln" tillsammans med i övrigt relativt tydliga regler så har det inneburit att allt fler kommuner och regioner nu börjat tillämpa eller åtminstone startat projekt för att tillämpa RKR R5.

80%-regeln: "Om nuvärdet av minimileaseavgifterna vid leasingperiodens början i allt väsentligt uppgår till tillgångens verkliga värde då ska leasingavtalet alltid redovisas som ett finansiellt avtal. I allt väsentligt definieras som 80% av leasingobjektets verkliga värde vid leasingperiodens början."

[img:thomas]

**Del 2: De fem stegen för att implementera RKR R5**

1. Samla den grundläggande kontraktsinformationen – Följande information behöver inhämtas oavsett om det är ett hyresavtal för en lokal, en maskin eller en bil.

2. Följ en process för utvärdering med kategorisering av kontrakt och beslut om kontrakten är finansiella eller operationella.

3. Iterera fram den implicita räntan med Excel- eller matematiska formler.

4. Välj strategi när implicit ränta inte kan användas – RKR R5 propagerar för att göra jobbet med marknadsvärde och ej garanterat restvärde.

5. Analysera de bokföringsmässiga konsekvenserna och skapa bokföringsunderlag.

**Del 3: Göra jobbet själv eller använda en systemlösning**

Många kommuner / regioner står inför valet att göra jobbet med att redovisa enligt RKR R5. Tiegos förslag är att genomföra steg 1 till 5 själv och sedan samarbeta med Tiego med steg 6-12.

Gör själv: 1. Inventera all grundläggande information, 2. Följ stegen för utvärdering, 3. Fatta beslut per kontrakt om informationen är tillräcklig för implicit ränta, 4. Välj bas för marginell låneränta, 5. Skapa förståelse för bokföringsmässig hantering.

Samarbeta med Tiego: 6. Skicka inventeringen, 7. Bestäm kontraktskategorier och konton, 8. Förbered för import av bokföringsorder, 9. Förbered för koncerneliminering, 10. Öva och förstå systemets hantering, 11. Analys och framtagande av underlag, 12. Presentation i årsredovisningen.

Tiego genomför gärna en workshop baserat på kontrakt som skickats till Tiego. Kontakta Tiego via "Boka demo" för att komma igång.`,
    contentEn: `Tiego has conducted a webinar on experiences from the implementation of RKR R5, organized by the Swedish Association of Municipal Economists (KEF). The Swedish Local Government Accounting Council (RKR) has for many years provided recommendations for leasing accounting, culminating in the publication of recommendation R5 in May 2023, followed by the idea paper "Leasing and Rental Agreement Accounting" in November 2023. Tiego has long provided solutions for the needs of businesses in group reporting and leasing accounting. Applying RKR is similar but has some distinct differences, which is why Tiego conducted this training. The goal was to give more municipalities and regions the opportunity to benefit from experiences and possibly consider using Tiego's systems and consulting services to comply with the regulations.

A survey was sent out before the training, with 14 municipalities/regions responding. Some of the questions and answers are summarized below. Responses were given with a number: 1: Not at all, 2: Partially, 3: Okay, 4: Good, 5: Very good.

[img:mattias]

**Part 1: Background and Regulations**

Question A: How well did the 2023 annual report meet the updated RKR R5?

Answer A: Average score 2.1, with six respondents answering "1: Not at all."

The purpose of RKR R5 is to create neutral and comparable accounting, regardless of how municipalities and regions choose to finance their resource acquisitions. When RKR included what we refer to as the "80%-rule" together with otherwise relatively clear rules, this has led to more municipalities and regions beginning to apply or at least start projects to apply RKR R5.

80%-Rule: "If the present value of the minimum lease payments at the beginning of the lease period essentially equals the fair value of the asset, the lease agreement must always be classified as a financial lease. Essentially means 80% of the asset's fair value at the beginning of the lease period."

In addition to the 80%-rule, R5 states that a rental agreement should also be considered financial if: 1) There is a purchase option, 2) The lease term covers the entire asset's life, 3) The asset has a special design or is of strategic importance, 4) Other situations/conditions exist that cause the lease agreement to be classified as financial.

[img:thomas]

**Part 2: The Five Steps for Implementing RKR R5**

1. Gather the Basic Contract Information – The following information needs to be collected, regardless of whether it is a rental agreement for a property, a machine, or a car: Company, Contract ID, Description, Categorization, Start period, End period, Period length, Prepaid or postpaid payment, Interest calculation method, Monthly cost.

2. Follow a process for evaluation with categorization of contracts and decisions on whether the contracts are financial or operational – To analyze whether the present value of future payments is greater than 80% of the market value, it is required to estimate the market value.

3. Iteratively calculate the implicit interest rate with Excel or mathematical formulas – Even if the municipality/region buys a system like the one Tiego offers, it is valuable to understand the mathematical formula for present value calculation.

4. Choose a strategy when the implicit interest rate cannot be used – RKR R5 advocates for using market value and non-guaranteed residual value to calculate the implicit interest rate, but if the inputs are misleading, this can lead to interest rates that are far from market-based. In such cases, a simpler method is to use the municipality's/region's alternative borrowing rate.

5. Analyze the accounting consequences and create accounting documentation – We recommend analyzing the accounting consequences already during the contract inventory, evaluating contracts based on both implicit interest rate and alternative borrowing rate side by side.

**Part 3: Do It Yourself or Use a System Solution**

Many municipalities/regions are faced with the choice of doing the work of accounting according to RKR R5 themselves or collaborating with a system provider. Tiego's suggestion is to carry out steps 1 to 5 yourself (possibly together with an accounting expert), and then collaborate with Tiego for steps 6-12.

Do it yourself: 1. Inventory all basic information, 2. Follow the steps for evaluation, 3. Decide per contract if the information is sufficient for the implicit interest rate, 4. Choose a base for the marginal borrowing rate, 5. Create an understanding of accounting handling.

Collaborate with Tiego: 6. Send the inventory, 7. Determine contract categories and accounts, 8. Prepare for importing accounting entries, 9. Prepare for group elimination of internal lease agreements, 10. Practice and understand how the system handles new contracts, changes, and complexities, 11. Analysis and creation of documentation, 12. Presentation in the annual report.

Tiego is happy to conduct a workshop based on contracts sent to Tiego in the Excel format presented in this webinar (or other readable formats). Contact Tiego via "Book a demo" to get access to the Excel file.`,
  },
  {
    id: "19",
    title: "Vi har ett nytt kontor!",
    titleEn: "We have a new office!",
    excerpt:
      "I fredags hade vi glädjen att inviga vår nya lokal, och vi vill rikta ett stort tack till alla som deltog och firade detta med oss. För er som missade tillfället, kan vi nu meddela att vi har flyttat till Sveavägen 68.",
    excerptEn: "Last Friday, we had the pleasure of inaugurating our new office, and we want to extend a big thank you to everyone who participated and celebrated with us. We have now moved to Sveavägen 68.",
    categories: ["Tiego"],
    date: "13 sep 2024",
    metaDescription: "Tiego har flyttat till Sveavägen 68 i Stockholm. Stort tack till alla som deltog när vi invigde den nya lokalen.",
    metaDescriptionEn: "Tiego has moved to Sveavägen 68 in Stockholm. A big thank you to everyone who joined us for the inauguration of our new office.",
    image: "news/kevin-studio.png",
    imageStyle: "cover",
    imageAlt: "Medarbetare på Tiego framför Tiego-logotypen på det nya kontoret",
    imageAltEn: "Tiego employee in front of the Tiego logo at the new office",
    content: `I fredags hade vi glädjen att inviga vår nya lokal, och vi vill rikta ett stort tack till alla som deltog och firade detta med oss. För er som missade tillfället, kan vi nu meddela att vi har flyttat till Sveavägen 68.`,
    contentEn: `Last Friday, we had the pleasure of inaugurating our new office, and we want to extend a big thank you to everyone who participated and celebrated with us. For those who missed the occasion, we are excited to announce that we have now moved to Sveavägen 68.`,
  },
  {
    id: "18",
    title: "Användardagen 2022",
    excerpt:
      "Årets användardag genomfördes digitalt den 14:e december med genomgång av budget och prognos för IFRS 16, processkarta för systemet i TLE, vidareutvecklad Edit Specification och mycket annat.",
    categories: ["TGA", "TLE"],
    date: "15 dec 2022",
    metaDescription: "Användardagen 2022 hölls digitalt den 14 december: budget och prognos för IFRS 16, processkarta för TLE, vidareutvecklad Edit Specification och mer.",
    metaDescriptionEn: "User Day 2022 was held digitally on 14 December: budget and forecast for IFRS 16, process map for TLE, an enhanced Edit Specification and more.",
    image: "news/tiego-integration.png",
    imageStyle: "contain",
    imageAlt: "Åhörare följer en presentation under Användardagen 2022",
    imageAltEn: "Attendee following a presentation at the 2022 user day",
    content: `Årets användardag genomfördes digitalt den 14:e december:

• Tiego Lease Engine: 09:00–12:00
• Tiego Group Accounting: 14:00–17:00

Genomgång av budget och prognos för IFRS 16, processkarta för systemet i TLE, vidareutvecklad Edit Specification, guidning till kassaflödet och Task Panes i TGA 7.6 och mycket annat. Filmer från dagen finns att se på web.tiego.se

**Tiego Lease Engine**

09:00–09:25: Systemkarta och processkarta för systemet (Mattias & Christian)

9:30–10:00: Årets IFRS-not med retrieve och Pivot inkl ny information om bruttobelopp uppskjuten skatt (Kevin & Christian)

10:00–10:25: Minskningar i kontrakt – med skillnad mellan minskning i legal och bedömd period. (Kevin & Thomas)

10:30–10:55: Kontrakt i utländsk valuta i tider av stora valutakurssvängningar (Kevin)

11:00–11:25: Budget och prognos för IFRS 16 (Christian)

11:30–11:55: Öppen tid för tillkommande frågor och fördjupning

**Tiego Group Accounting**

14:00–14:25: Systemkarta och ökade krav på revisorer med ISA 315 (Mattias)

14:30–14:55: Version 7.6 med vidareutvecklad Edit Specification, guidning till kassaflödet och Task Panes. Vidareutvecklad process för release och test. (Erik, Peter & Sanna)

15:00–15:25: Erfarenhetsutbyte i arbete med retrieverapporter och kommande funktionalitetsutveckling. (Erik & Peter)

15:30–15:55: ABC-allokering av kostnader för funktionsorienterad resultaträkning (Christian)

16:00–16:25: Vidareutvecklad fusionsfunktionalitet (Mattias)

16:30–16:55: Öppen tid för tillkommande frågor och fördjupning

Tack till alla som deltog!`,
    titleEn: "User Day 2022",
    excerptEn: "This year's user day was held digitally on December 14th with a review of budget and forecast for IFRS 16, process map for the system in TLE, further developed Edit Specification and much more.",
    contentEn: `This year's user day was held digitally on December 14th:

• Tiego Lease Engine: 09:00-12:00
• Tiego Group Accounting: 14:00-17:00

Review of budget and forecast for IFRS 16, process map for the system in TLE, further developed Edit Specification, guidance on cash flow and Task Panes in TGA 7.6 and much more. Videos from the day can be viewed at web.tiego.se

**Tiego Lease Engine**

09:00-09:25: System map and process map for the system (Mattias & Christian)

9:30-10:00: This year's IFRS note with retrieve and Pivot including new information on gross amount deferred tax (Kevin & Christian)

10:00-10:25: Reductions in contracts - with differences between reduction in legal and estimated period. (Kevin & Thomas)

10:30-10:55: Contracts in foreign currency in times of large exchange rate fluctuations (Kevin)

11:00-11:25: Budget and forecast for IFRS 16 (Christian)

11:30-11:55: Open time for additional questions and deepening

**Tiego Group Accounting**

14:00-14:25: System map and increased requirements for auditors with ISA 315 (Mattias)

14:30-14:55: Version 7.6 with further developed Edit Specification, guidance on cash flow and Task Panes. Further developed process for release and test. (Erik, Peter & Sanna)

15:00-15:25: Exchange of experiences in work with retrieve reports and upcoming functionality development. (Erik & Peter)

15:30-15:55: ABC allocation of costs for function-oriented income statement (Christian)

16:00-16:25: Further developed fusion functionality (Mattias)

16:30-16:55: Open time for additional questions and deepening

**Thank you to everyone who participated!**`,
  },
  {
    id: "17",
    title: "IFRS Symposiet 2022 med Mattias Elfgren & Tobias Stråhle från PwC",
    titleEn: "The IFRS Symposium 2022 with Mattias Elfgren & Tobias Stråhle from PwC",
    excerpt:
      "Tiego deltog tillsammans med PwC på IFRS Symposiet och bjöd på en matnyttig presentation om omvända förvärv och prospekt i praktiken.",
    excerptEn: "Tiego participated with PwC in the IFRS Symposium and offered an informative presentation on reverse acquisitions and prospectus in practice.",
    categories: ["TGA"],
    date: "14 sep 2022",
    image: "news/mattias-vit3.png",
    imageStyle: "portrait",
    imageAlt: "Porträtt av Mattias Elfgren, VD på Tiego",
    imageAltEn: "Portrait of Mattias Elfgren, CEO of Tiego",
    vimeoId: "751672164",
    content: `**En presentation om omvända förvärv och prospekt i praktiken.**

Tiego deltog i år tillsammans med Pwc på IFRS Symposiet och bjöd på en matnyttig presentation om "omvända förvärv och prospekt i praktiken". Tobias Stråhle från Pwc som besitter en lång erfarenhet av just "omvända förvärv" går i presentation närmare in på hur han ser på ett omvänt förvärv och vilka problem som kan uppstå under processen. Vi tar även del av en djup kunskapsrikedom i båda frågorna från Mattias Elfgren som presenterar ett flertal systembaserade lösningsalternativ till båda arbetsprocesserna.

En fullspäckad presentation av båda parter som delar med sig av sina tankar, erfarenheter och välbeprövade arbetsmetoder.`,
    contentEn: `**A presentation on reverse acquisitions and prospectus in practice.**

This year, Tiego participated with PwC in the IFRS Symposium and offered an informative presentation on "reverse acquisitions and prospectus in practice". Tobias Stråhle from PwC, who has extensive experience with reverse acquisitions, delves into how he views a reverse acquisition and the problems that can arise during the process. We also gain deep knowledge in both areas from Mattias Elfgren, who presents several system-based solution alternatives for both work processes.

A packed presentation by both parties who share their thoughts, experiences, and proven working methods.`,
  },
  {
    id: "16",
    title: "Finansiell leasing enligt RKR R5 i praktiken på Kommek 2022",
    titleEn: "Financial Leasing According to RKR R5 in Practice at Kommek 2022",
    excerpt:
      "Aspia och Tiego deltog på Kommek 2022 i Malmö med en presentation om RKR R5. Marcus Johansson, Mattias Elfgren och Kevin Hall tog upp hur regelverket vuxit fram och redovisningstekniken i praktiken.",
    excerptEn: "Aspia and Tiego participated in Kommek 2022 in Malmö with a presentation on RKR R5. Marcus Johansson, Mattias Elfgren, and Kevin Hall discussed how the regulations have evolved and the accounting technique in practice.",
    categories: ["TLE"],
    date: "17 aug 2022",
    heading: "RKR R5 i praktiken på Kommek 2022",
    headingEn: "RKR R5 in Practice at Kommek 2022",
    metaDescription: "Aspia och Tiego presenterade RKR R5 på Kommek 2022 i Malmö: hur regelverket för finansiell leasing vuxit fram och redovisningstekniken i praktiken.",
    metaDescriptionEn: "Aspia and Tiego presented RKR R5 at Kommek 2022 in Malmö: how the rules for financial leasing evolved and the accounting technique in practice.",
    image: "news/kommek-logo.png",
    imageStyle: "contain",
    imageAlt: "Kommek logotyp",
    imageAltEn: "Kommek logo",
    vimeoId: "743032276",
    content: `**Presentation av Aspia & Tiego gällande RKR R5**

Aspia och Tiego deltog på Kommek 2022 i Malmö med en presentation om RKR R5. Marcus Johansson, Mattias Elfgren och Kevin Hall tog upp hur regelverket vuxit fram och redovisningstekniken i praktiken. De tog också upp något av det som tagits upp i revisionsrapporter och kom också med förslag på hur transparensen skulle kunna öka om notinformationen om leasing utökas.

Se gärna filmen (26 minuter) som har följande agenda:

Leasing enligt K3, Mattias Elfgren, Tiego

Leasing enligt IFRS, Kevin Hall, Tiego

Leasing enligt RKR R5, Marcus Johansson, Aspia

Revisionsrapporter om finansiell leasing

Offerter & workshop erbjuds`,
    contentEn: `**Presentation by Aspia & Tiego on RKR R5**

Aspia and Tiego participated in Kommek 2022 in Malmö with a presentation on RKR R5. Marcus Johansson, Mattias Elfgren, and Kevin Hall discussed how the regulations have evolved and the accounting technique in practice. They also addressed some of the issues raised in audit reports and made suggestions on how transparency could be increased if the leasing-related note information is expanded.

Please watch the 26-minute video with the following agenda:

Leasing according to K3, Mattias Elfgren, Tiego

Leasing according to IFRS, Kevin Hall, Tiego

Leasing according to RKR R5, Marcus Johansson, Aspia

Audit reports on financial leasing

Quotes & workshops are offered`,
  },
  {
    id: "15",
    title: "Redovisning av minskningar enligt IFRS 16",
    excerpt:
      "Konsulter från Tiego deltog på FAR-dagen i november där många var överens om att ändringar i leasingkontrakt är svårt och särskilt minskningar av kontrakt.",
    categories: ["TLE", "IFRS 16"],
    date: "7 dec 2020",
    metaDescriptionEn: "Tiego's consultants attended the FAR day in November, where many agreed that lease contract changes, especially reductions, are difficult under IFRS 16.",
    image: "news/ifrs-books.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på bokryggarna till IFRS-standarderna",
    imageAltEn: "Close-up of the spines of the IFRS standards volumes",
    titleEn: "Accounting for Reductions According to IFRS 16",
    excerptEn: "Consultants from Tiego participated in the FAR day in November where many agreed that changes in lease contracts are difficult, especially reductions in contracts.",
    content: `**Enkät och anmälan till webbinarium 16 december**

Konsulter från Tiego deltog på FAR-dagen i november där många var överens om att ändringar i leasingkontrakt är svårt och särskilt minskningar av kontrakt. Det var till och med så att det sades av experter att de ännu inte sett någon leverantör som kan hantera olika kombinationer helt korrekt. Det tog vi som en utmaning att visa att det inte stämmer! Vi skulle vilja bjuda in dig till ett frukostwebbinarium den 16 december 2020 kl 8:30-ca 9:15 där vi kommer gå igenom två exempel på minskningar av kontrakt.`,
    contentEn: `**Survey and Registration for Webinar on December 16**

Consultants from Tiego participated in the FAR day in November where many agreed that changes in lease contracts are difficult, especially reductions in contracts. It was even said by experts that they have not yet seen any provider that can handle different combinations completely correctly. We took this as a challenge to show that it is not true! We would like to invite you to a breakfast webinar on December 16, 2020, at 8:30–9:15 am where we will go through two examples of reductions in contracts.`,
  },
  {
    id: "14",
    title: "XBRL-taggning med EasyX och Teqnions årsredovisning 2019 som exempel",
    excerpt:
      "På tisdag 29 september kl 9:00–ca 10:30 genomför Tiego i samarbete med EasyX ytterligare en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar.",
    categories: ["TGA"],
    date: "15 sep 2020",
    heading: "XBRL-taggning med EasyX: Teqnion",
    headingEn: "XBRL Tagging with EasyX: Teqnion",
    metaDescription: "Den 29 september demonstrerar Tiego och EasyX XBRL-taggning av årsredovisningar med Teqnions årsredovisning 2019 som exempel.",
    metaDescriptionEn: "On 29 September Tiego and EasyX demonstrate XBRL tagging of annual reports, using Teqnion's 2019 annual report as an example.",
    image: "news/xbrl-logo.jpg",
    imageStyle: "contain",
    imageAlt: "XBRL logotyp – The Business Reporting Standard",
    imageAltEn: "XBRL logo – The Business Reporting Standard",
    titleEn: "XBRL Tagging Using EasyX and Teqnion's Annual Report 2019 as an Example",
    excerptEn: "On Tuesday, September 29th, Tiego will be conducting, in collaboration with EasyX, another demonstration of EasyX's solution for XBRL tagging of annual reports.",
    content: `**Enkät och anmälan till webbinarium 29 september**

På tisdag 29 september kl 9:00—ca 10:30 genomför Tiego i samarbete med EasyX ytterligare en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar. Som exempel har Karsten och hans team använt Teqnions årsredovisning för 2019. Demonstrationen är en webbsession med Zoom med ljud och bild från din dator där det kommer finnas tillfälle att ställa frågor.`,
    contentEn: `**Survey and Registration for Webinar on September 29th**

On Tuesday, September 29th at 9:00 am – approximately 10:30 am, Tiego will be conducting, in collaboration with EasyX, another demonstration of EasyX's solution for XBRL tagging of annual reports. As an example, Karsten and his team have used Teqnion's 2019 annual report. The demonstration is a web session with Zoom, including audio and video from your computer, where there will be an opportunity to ask questions.`,
  },
  {
    id: "13",
    title: "IFRIC-uttalanden om IFRS 16 och erfarenheter från drift",
    excerpt:
      "Nu 1,5 år efter införandet av IFRS 16 är det många användare, revisorer och systemleverantörer som samlat på sig erfarenheter som vi tror är intressanta att diskutera.",
    categories: ["TGA", "IFRS 16"],
    date: "13 jul 2020",
    metaDescription: "1,5 år efter införandet av IFRS 16 har användare, revisorer och systemleverantörer samlat erfarenheter värda att diskutera. IFRIC-uttalanden och drift.",
    metaDescriptionEn: "1.5 years after IFRS 16 took effect, users, auditors and system providers have gained experience worth discussing. IFRIC statements and operations.",
    image: "news/ifrs16-book.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på en sida ur IFRS 16 med illustrativa exempel",
    imageAltEn: "Close-up of a page from the IFRS 16 illustrative examples",
    titleEn: "IFRIC Statements on IFRS 16 and Operational Experiences",
    excerptEn: "Now, 1.5 years after the implementation of IFRS 16, many users, auditors, and system providers have gained experiences that we believe are interesting to discuss.",
    content: `**Enkät och anmälan till webbinarium 21 september**

Nu 1,5 år efter införandet av IFRS 16 är det många användare, revisorer och systemleverantörer som samlat på sig erfarenheter som vi tror är intressanta att diskutera. Välkommen att bidra med dina erfarenheter i vår enkät och att anmäla dig till vårt webbinarium måndag 21 september kl 8:30-9:15. På tisdag 22 september går sedan IFRS-Symposiet av stapeln, där vi är utställare, och vi hoppas att se många IFRS-tillämpare där fysiskt eller digitalt.`,
    contentEn: `**Survey and Registration for Webinar on September 21st**

Now, 1.5 years after the implementation of IFRS 16, many users, auditors, and system providers have gained experiences that we believe are interesting to discuss. You are welcome to contribute your experiences in our survey and to register for our webinar on Monday, September 21st, from 8:30 to 9:15. On Tuesday, September 22nd, the IFRS Symposium will take place, where we are exhibitors, and we hope to see many IFRS practitioners there, either physically or digitally.`,
  },
  {
    id: "12",
    title: "XBRL-taggning med EasyX och Stockwik Förvaltnings årsredovisning 2019 som exempel",
    excerpt:
      "På tisdag 9 juni kl 10:00–ca 11:30 genomför Tiego i samarbete med EasyX ytterligare en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar.",
    categories: ["TGA"],
    date: "1 jun 2020",
    heading: "XBRL-taggning med EasyX: Stockwik",
    headingEn: "XBRL Tagging with EasyX: Stockwik",
    metaDescription: "Den 9 juni demonstrerar Tiego och EasyX XBRL-taggning av årsredovisningar med Stockwik Förvaltnings årsredovisning 2019 som exempel.",
    metaDescriptionEn: "On 9 June Tiego and EasyX demonstrate XBRL tagging of annual reports, using Stockwik Förvaltning's 2019 annual report as an example.",
    image: "news/xbrl-logo.jpg",
    imageStyle: "contain",
    imageAlt: "XBRL logotyp – The Business Reporting Standard",
    imageAltEn: "XBRL logo – The Business Reporting Standard",
    titleEn: "XBRL Tagging with EasyX and Stockwik Förvaltnings Annual Report 2019 as an Example",
    excerptEn: "On Tuesday, June 9th, at 10:00 am to about 11:30 am, Tiego, in collaboration with EasyX, will hold another demonstration of EasyX's solution for XBRL tagging of annual reports.",
    content: `**Enkät och anmälan till webbinarium 9 juni**

På tisdag 9 juni kl 10:00—ca 11:30 genomför Tiego i samarbete med EasyX ytterligare en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar. Som exempel har Karsten och hans team använt Stockwik Förvaltnings årsredovisning för 2019. Demonstrationen är en webbsession med Zoom med ljud och bild från din dator där det kommer finnas tillfälle att ställa frågor.`,
    contentEn: `**Survey and Registration for Webinar on June 9th**

On Tuesday, June 9th, at 10:00 am to about 11:30 am, Tiego, in collaboration with EasyX, will hold another demonstration of EasyX's solution for XBRL tagging of annual reports. As an example, Karsten and his team have used Stockwik Förvaltning's annual report for 2019. The demonstration is a web session with Zoom with audio and video from your computer where there will be an opportunity to ask questions.`,
  },
  {
    id: "11",
    title: "TLE Admin Nyheter",
    excerpt:
      "Nu välkomnar vi anmälningar av TLE-administratörer till TLE Admin Nyheter onsdag 13 maj kl 10 – ca 12. Seminariet är en Zoom webbsession med deltagare inloggade med webbkamera (kostnadsfritt).",
    categories: ["TLE"],
    date: "6 maj 2020",
    metaDescription: "TLE Admin Nyheter onsdag 13 maj kl 10–12: kostnadsfri Zoom-session för TLE-administratörer om nyheter i Tiego Lease Engine.",
    metaDescriptionEn: "TLE Admin News, Wednesday 13 May 10–12: a free Zoom session for TLE administrators on what's new in Tiego Lease Engine.",
    image: "news/tle-box.jpg",
    imageStyle: "contain",
    imageAlt: "Produktbox för Tiego Lease Engine version 7",
    imageAltEn: "Product box for Tiego Lease Engine version 7",
    titleEn: "TLE Admin News",
    excerptEn: "We now welcome registrations from TLE administrators for TLE Admin News on Wednesday, May 13 at 10am – approximately 12pm. The seminar is a Zoom web session with participants logged in with a webcam (free of charge).",
    content: `**Enkät och anmälan till webbinarium 13 maj**\n\nNu välkomnar vi anmälningar av TLE-administratörer till TLE Admin Nyheter onsdag 13 maj kl 10 – ca 12. Seminariet är en Zoom webbsession med deltagare inloggade med webbkamera (kostnadsfritt).`,
    contentEn: `**Survey and Registration for Webinar on May 13**\n\nWe now welcome registrations from TLE administrators for TLE Admin News on Wednesday, May 13 at 10am – approximately 12pm. The seminar is a Zoom web session with participants logged in with a webcam (free of charge).`,
  },
  {
    id: "10",
    title: "TGA Admin Nyheter",
    excerpt:
      "Nu välkomnar vi anmälningar av TGA-administratörer till TGA Admin Nyheter tisdag 12 maj kl 10– ca 12. Seminariet är en Zoom webbsession med deltagare inloggade med webbkamera (kostnadsfritt).",
    categories: ["TGA"],
    date: "6 maj 2020",
    metaDescription: "TGA Admin Nyheter tisdag 12 maj kl 10–12: kostnadsfri Zoom-session för TGA-administratörer om nyheter i Tiego Group Accounting.",
    metaDescriptionEn: "TGA Admin News, Tuesday 12 May 10–12: a free Zoom session for TGA administrators on what's new in Tiego Group Accounting.",
    image: "news/tle-box.jpg",
    imageStyle: "contain",
    imageAlt: "Produktbox för Tiego Lease Engine version 7",
    imageAltEn: "Product box for Tiego Lease Engine version 7",
    titleEn: "TGA Admin News",
    excerptEn: "We now welcome registrations from TGA administrators for TGA Admin News on Tuesday, May 12th at 10am – approx. 12pm. The seminar is a Zoom web session with participants logged in with webcam (free of charge).",
    content: `**Enkät och anmälan till webbinarium 12 maj**\n\nNu välkomnar vi anmälningar av TGA-administratörer till TGA Admin Nyheter tisdag 12 maj kl 10– ca 12. Seminariet är en Zoom webbsession med deltagare inloggade med webbkamera (kostnadsfritt).`,
    contentEn: `**Survey and Registration for Webinar on May 12th**\n\nWe now welcome registrations from TGA administrators for TGA Admin News on Tuesday, May 12th at 10am – approx. 12pm. The seminar is a Zoom web session with participants logged in with webcam (free of charge).`,
  },
  {
    id: "9",
    title: "Koncernrapportering i Excel med BAS-inspirerad kontoplan – hur långt räcker det?",
    excerpt:
      "På onsdag 27 maj kl 8:30–9:15 genomför vi ett webbinarium som riktar sig till koncernekonomer som endera byggt Excel-lösningar för rapportering och/eller funderar på att byta affärssystem.",
    categories: ["TGA"],
    date: "6 maj 2020",
    heading: "Koncernrapportering i Excel",
    headingEn: "Group Reporting in Excel",
    metaDescription: "Webbinarium 27 maj för koncernekonomer som byggt Excel-lösningar för rapportering eller funderar på att byta affärssystem. Hur långt räcker Excel?",
    metaDescriptionEn: "Webinar on 27 May for group accountants who built Excel solutions for reporting or are considering a new business system. How far does Excel go?",
    image: "news/excel-icon.jpg",
    imageStyle: "contain",
    imageAlt: "Microsoft Excel-ikon",
    imageAltEn: "Microsoft Excel icon",
    titleEn: "Group Reporting in Excel with a BAS-Inspired Chart of Accounts – How Far Does It Go?",
    excerptEn: "On Wednesday, May 27 at 8:30–9:15 am, we will conduct a webinar aimed at group accountants who have either built Excel solutions for reporting and/or are considering changing business systems.",
    content: `**Enkät och anmälan till webbinarium om erfarenheter av affärssystem 27 maj**\n\nPå onsdag 27 maj kl 8:30–9:15 genomför vi ett webbinarium som riktar sig till koncernekonomer som endera byggt Excel-lösningar för rapportering och/eller funderar på att byta affärssystem. Inför webbinariet så har vi tagit fram en enkät för att samla in erfarenheter från olika system. Svara gärna på frågorna och anmäl dig nedan.`,
    contentEn: `**Survey and Registration for Webinar on Business System Experiences – May 27**\n\nOn Wednesday, May 27 at 8:30–9:15 am, we will conduct a webinar aimed at group accountants who have either built Excel solutions for reporting and/or are considering changing business systems. Prior to the webinar, we have created a survey to gather experiences from different systems. Please answer the questions and register below.`,
  },
  {
    id: "8",
    title: "XBRL-taggning inför rapportering till Finansinspektionen 2021 med EasyX, med en Tiego-kund som exempel.",
    excerpt:
      "På torsdag 28 maj kl 10:00–ca 11:30 genomför Tiego i samarbete med EasyX en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar.",
    categories: ["TGA"],
    date: "6 maj 2020",
    heading: "XBRL-taggning inför FI-rapportering 2021",
    headingEn: "XBRL Tagging for FSA Reporting 2021",
    metaDescription: "Den 28 maj demonstrerar Tiego och EasyX XBRL-taggning av årsredovisningar inför rapportering till Finansinspektionen 2021, med en Tiego-kund som exempel.",
    metaDescriptionEn: "On 28 May Tiego and EasyX demonstrate XBRL tagging of annual reports ahead of 2021 reporting to the Swedish FSA, with a Tiego customer as an example.",
    image: "news/xbrl-logo.jpg",
    imageStyle: "contain",
    imageAlt: "XBRL logotyp – The Business Reporting Standard",
    imageAltEn: "XBRL logo – The Business Reporting Standard",
    titleEn: "XBRL Tagging for Reporting to the Financial Supervisory Authority 2021 with EasyX, with a Tiego Customer as an Example",
    excerptEn: "On Thursday, May 28 at 10:00–11:30 am, Tiego, in collaboration with EasyX, will conduct a demonstration of EasyX's solution for XBRL tagging of annual reports.",
    content: `**Enkät och anmälan till webbinarium 28 maj**\n\nPå torsdag 28 maj kl 10:00—ca 11:30 genomför Tiego i samarbete med EasyX en demonstration av EasyX:s lösning för XBRL-taggning av årsredovisningar. Som exempel har Karsten och hans team använt Genova Property Groups årsredovisning. Demonstrationen är en webbsession med Zoom med ljud och bild från din dator där det kommer finnas tillfälle att ställa frågor.`,
    contentEn: `**Survey and Registration for Webinar on May 28**\n\nOn Thursday, May 28 at 10:00–11:30 am, Tiego, in collaboration with EasyX, will conduct a demonstration of EasyX's solution for XBRL tagging of annual reports. As an example, Karsten and his team have used Genova Property Group's annual report. The demonstration is a web session with Zoom with audio and video from your computer where there will be an opportunity to ask questions.`,
  },
  {
    id: "7",
    title: "Bokslutsrutiner och kassaflöde enligt IB-UB-metoden",
    excerpt:
      "Vår uppfattning är att det nästan alltid är två saker som är kvar till slutet av en bokslutsprocess: skattespecifikationen och kassaflödet.",
    categories: ["TGA"],
    date: "2 apr 2020",
    image: "news/ib-ub.jpg",
    imageStyle: "contain",
    imageAlt: "Grafik med texten IB–UB",
    imageAltEn: "Graphic with the text IB–UB",
    titleEn: "Closing Procedures and Cash Flow According to the IB-UB Method",
    excerptEn: "Our perception is that there are almost always two things left at the end of a closing process: the tax specification and the cash flow.",
    content: `**Enkät och anmälan till webbinarium 22 april**\n\nVår uppfattning är att det nästan alltid är två saker som är kvar till slutet av en bokslutsprocess: skattespecifikationen och kassaflödet. Är du intresserad av att lyssna på våra erfarenheter och kanske bidra med dina i vår enkät? Den 22 april kl 8:30—ca 9:15 genomför vi ett webbinarium om bokslutsrutiner och kassaflöde. Anmäl dig senast tisdag 21 april.`,
    contentEn: `**Survey and Registration for Webinar on April 22**\n\nOur perception is that there are almost always two things left at the end of a closing process: the tax specification and the cash flow. Are you interested in listening to our experiences and maybe contributing with yours in our survey? On April 22 at 8:30–9:15 am, we will conduct a webinar on closing procedures and cash flow. Register by Tuesday, April 21.`,
  },
  {
    id: "6",
    title: "PDF 1 april 2020: Enkät och remissvar pga nya IAS 1",
    excerpt:
      "Tack för ert deltagande vid dagens webbinarium: Enkät & remissvar pga nya IAS 1. Nu har ni även möjlighet att ladda ner presentationen i pdf-format.",
    categories: ["TGA"],
    date: "1 apr 2020",
    metaDescriptionEn: "Thank you for taking part in today's webinar on the survey and comment letter on the new IAS 1. The presentation is now available for download as a PDF.",
    image: "news/ias1-enkat.jpg",
    imageStyle: "cover",
    imageAlt: "Surfplatta på ett skrivbord med texten IAS 1 Enkät",
    imageAltEn: "Tablet on a desk showing the text IAS 1 Survey",
    titleEn: "PDF April 1, 2020: Survey and Comments Due to New IAS 1",
    excerptEn: "Thank you for your participation in today's webinar: Survey & comments due to new IAS 1. Now you also have the opportunity to download the presentation in pdf format.",
    content: `**Presentation från webbinarium 4 april**\n\nTack för ert deltagande vid dagens webbinarium: Enkät & remissvar pga nya IAS 1. Nu har ni även möjlighet att ladda ner presentationen i pdf-format. Tack för ert visade intresse.`,
    contentEn: `**Presentation from Webinar on April 4**\n\nThank you for your participation in today's webinar: Survey & comments due to new IAS 1. Now you also have the opportunity to download the presentation in pdf format. Thank you for your interest.`,
  },
  {
    id: "5",
    title: "Följdfrågor om jämförelsestörande poster inför webbinarium",
    excerpt:
      "Tiego har tidigare skickat ut en enkät inför remissvar om nya IAS 1 från IASB. Som ett komplement till enkäten har vi ställt samman ett antal följdfrågor avseende jämförelsestörande poster.",
    categories: ["TGA"],
    date: "16 mar 2020",
    metaDescription: "Som komplement till enkäten inför remissvaret om nya IAS 1 har Tiego ställt samman följdfrågor om jämförelsestörande poster inför webbinariet.",
    metaDescriptionEn: "As a complement to the survey ahead of the comment letter on the new IAS 1, Tiego has compiled follow-up questions on comparative items for the webinar.",
    image: "news/inspelning.jpg",
    imageStyle: "cover",
    imageAlt: "Kevin Hall, Tiego, framför kameran under en inspelning",
    imageAltEn: "Kevin Hall, Tiego, in front of the camera during a recording",
    titleEn: "Follow-up Questions About Comparative Items Prior to Webinar",
    excerptEn: "Tiego has previously sent out a survey in connection with comments due to the new IAS 1 from the IASB. As a complement to the survey, we have put together a number of follow-up questions regarding comparative items.",
    content: `**Enkät och anmälan till webbinarium 1 april**\n\nTiego har tidigare skickat ut en enkät inför remissvar om nya IAS 1 från IASB (https://www.tiego.se/enkat-iasb). Som ett komplement till enkäten har vi ställt samman ett antal följdfrågor avseende jämförelsestörande poster. Just jämförelsestörande poster tror vi är det område som kommer vålla mest huvudbry i att tillämpa de nya reglerna. Om du besvarar frågorna så bidrar du till erfarenhetsutbytet på webinariet!`,
    contentEn: `**Survey and Registration for Webinar on April 1**\n\nTiego has previously sent out a survey in connection with comments due to the new IAS 1 from the IASB (https://www.tiego.se/enkat-iasb). As a complement to the survey, we have put together a number of follow-up questions regarding comparative items. We believe that comparative items are the area that will cause the most headaches in applying the new rules. If you answer the questions, you will contribute to the exchange of experience in the webinar!`,
  },
  {
    id: "4",
    title: "Enkät IASB inför live-sändning av seminarium",
    excerpt:
      "IASB har skickat ut önskemål om att få svar på sin remiss \"General Presentation and Disclosures – Comments to be received by 30 June 2020\".",
    categories: ["TGA"],
    date: "16 mar 2020",
    image: "news/ias1-enkat.jpg",
    imageStyle: "cover",
    imageAlt: "Surfplatta på ett skrivbord med texten IAS 1 Enkät",
    imageAltEn: "Tablet on a desk showing the text IAS 1 Survey",
    titleEn: "Survey IASB Prior to Live Broadcast of Seminar",
    excerptEn: "IASB has requested responses to its comment letter \"General Presentation and Disclosures – Comments to be received by 30 June 2020\".",
    content: `**Enkät och anmälan till webbinarium 1 april**\n\nIASB har skickat ut önskemål om att få svar på sin remiss "General Presentation and Disclosures – Comments to be received by 30 June 2020".\n\nVi skulle bli mycket glada om du hade möjlighet att svara på några frågor och/eller ville lyssna på vårt seminarium i ämnet. Observera att seminariet är en live-sändning på webben onsdag den 1 april kl 8:30-10 istället för hos oss på Birger Jarlsgatan pga Nya Corona / Covid-19. Anmäl dig senast fredag 27:e mars.`,
    contentEn: `**Survey and Registration for Webinar on April 1**\n\nIASB has requested responses to its comment letter "General Presentation and Disclosures – Comments to be received by 30 June 2020".\n\nWe would be very happy if you could answer a few questions and/or listen to our seminar on the subject. Note that the seminar is a live webcast on Wednesday, April 1 at 8:30–10 am instead of at our Birger Jarlsgatan location due to the new Corona/Covid-19. Register by Friday, March 27.`,
  },
  {
    id: "3",
    title: "Noten till IFRS 16 i praktiken",
    excerpt:
      "Välkomna på frukostseminarium hos Tiego, onsdagen den 15:e januari kl 8 – 10. Presentationen på seminariet tar sin utgångspunkt i PwC:s exempelårsredovisning.",
    categories: ["TLE", "IFRS 16"],
    date: "7 jan 2020",
    metaDescription: "Frukostseminarium hos Tiego onsdag 15 januari kl 8–10 om noten till IFRS 16 i praktiken, med utgångspunkt i PwC:s exempelårsredovisning.",
    metaDescriptionEn: "Breakfast seminar at Tiego on Wednesday 15 January, 8–10 am, on the IFRS 16 note in practice, based on PwC's illustrative financial statements.",
    image: "news/ifrs16-book2.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på en sida ur IFRS 16 med illustrativa exempel",
    imageAltEn: "Close-up of a page from the IFRS 16 illustrative examples",
    titleEn: "Note to IFRS 16 in Practice",
    excerptEn: "Welcome to a breakfast seminar at Tiego on Wednesday, January 15 at 8–10 am. The presentation at the seminar is based on PwC's example financial statements.",
    content: `**Anmälan till frukostseminarium 15 januari**\n\nVälkomna på frukostseminarium hos Tiego, onsdagen den 15:e januari kl 8 – 10. Presentationen på seminariet tar sin utgångspunkt i PwC:s exempelårsredovisning och vi har också läst ett antal förtidstillämpares årsredovisningar för att kunna ge användare inspiration till hur man kan utforma notinformationen. Vi kommer bl.a. diskutera att flera förtidstillämpare ger information om potentiell skuld när det finns en option att hyra vidare (för de fall bolaget inte är rimligt säker på att förlänga).`,
    contentEn: `**Registration for Breakfast Seminar on January 15**\n\nWelcome to a breakfast seminar at Tiego on Wednesday, January 15 at 8–10 am. The presentation at the seminar is based on PwC's example financial statements, and we have also read a number of early adopters' financial statements to provide users with inspiration on how to design the note information. We will discuss, among other things, that several early adopters provide information about potential liabilities when there is an option to continue leasing (for cases where the company is not reasonably certain to extend).`,
  },
  {
    id: "2",
    title: "Revisionen av IFRS 16 närmar sig",
    titleEn: "The audit of IFRS 16 is approaching",
    excerpt:
      "Vill du träffa andra ekonomer som implementerat IFRS 16? Välkomna i så fall till konferensavdelningen på Birger Jarlsgatan 57 torsdagen den 26:e september.",
    excerptEn:
      "Would you like to meet other economists who have implemented IFRS 16? If so, welcome to the conference department at Birger Jarlsgatan 57 on Thursday, September 26.",
    categories: ["IFRS 16"],
    date: "10 sep 2019",
    metaDescriptionEn: "Meet other finance professionals who have implemented IFRS 16: welcome to the conference floor at Birger Jarlsgatan 57 on Thursday 26 September.",
    image: "news/ifrs16-book2.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på en sida ur IFRS 16 med illustrativa exempel",
    imageAltEn: "Close-up of a page from the IFRS 16 illustrative examples",
    content: `**Anmälan till seminarium 26 september**\n\nVill du träffa andra ekonomer som implementerat IFRS 16? Välkomna i så fall till konferensavdelningen på Birger Jarlsgatan 57 torsdagen den 26:e september för att träffa användare, IFRS-experter och oss på Tiego för genomgång av erfarenheter inför revisionen av IFRS 16.`,
    contentEn: `**Registration for seminar on September 26**\n\nWould you like to meet other economists who have implemented IFRS 16? If so, welcome to the conference department at Birger Jarlsgatan 57 on Thursday, September 26 to meet users, IFRS experts, and us at Tiego for a review of experiences ahead of the revision of IFRS 16.`,
  },
  {
    id: "2b",
    title: "Erfarenheter från implementation av IFRS 16 med full retroaktivitet",
    titleEn: "Experiences from implementation of IFRS 16 with full retroactivity",
    excerpt:
      "Nu när övergången till IFRS 16 är avklarad för de flesta börjar jobbet med att tillämpa regelverket under innevarande år. I denna presentation redovisar Tiego och Scandi Standard sina erfarenheter från övergång med full retroaktivitet.",
    excerptEn:
      "Now that the transition to IFRS 16 is completed for most, the job of applying the rules during the current year begins. In this presentation, Tiego and Scandi Standard share their experiences from the transition with full retroactivity.",
    categories: ["TLE", "IFRS 16"],
    date: "25 apr 2019",
    heading: "IFRS 16 med full retroaktivitet",
    headingEn: "IFRS 16 with Full Retroactivity",
    metaDescription: "Tiego och Scandi Standard delar erfarenheter från övergången till IFRS 16 med full retroaktivitet – och jobbet med att tillämpa regelverket löpande.",
    metaDescriptionEn: "Tiego and Scandi Standard share their experiences from the transition to IFRS 16 with full retrospective application and applying the standard.",
    image: "news/ifrs-books.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på bokryggarna till IFRS-standarderna",
    imageAltEn: "Close-up of the spines of the IFRS standards volumes",
    content: `**Inspelning av seminarium 25 april**\n\nNu när övergången till IFRS 16 är avklarad för de flesta börjar jobbet med att tillämpa regelverket under innevarande år. Då ska allt från indexökningar och förlängningar till mer komplexa kontraktshändelser hanteras. I denna presentation redovisar Tiego och Scandi Standard sina erfarenheter från övergång med full retroaktivitet då reglerna tillämpats även historiskt.`,
    contentEn: `**Recording of seminar on April 25**\n\nNow that the transition to IFRS 16 is completed for most, the job of applying the rules during the current year begins. This involves handling everything from index increases and extensions to more complex contract events. In this presentation, Tiego and Scandi Standard share their experiences from the transition with full retroactivity, where the rules have been applied historically as well.`,
  },
  {
    id: "1",
    title: "Erfarenheter från kartläggning och driftsättning av IFRS 16",
    titleEn: "Experiences from mapping and implementation of IFRS 16",
    excerpt:
      "Med den nya IFRS-standarden för leasingavtal, IFRS 16, som trädde i kraft den 1 januari 2019, berörs samtliga IFRS bolag som har hyresavtal i sin verksamhet.",
    excerptEn:
      "With the new IFRS standard for lease agreements, IFRS 16, which came into force on January 1, 2019, all IFRS companies that have lease agreements in their operations are affected.",
    categories: ["TLE", "IFRS 16"],
    date: "11 apr 2019",
    metaDescription: "IFRS 16 trädde i kraft 1 januari 2019 och berör alla IFRS-bolag med hyresavtal. Tiegos erfarenheter från kartläggning och driftsättning.",
    metaDescriptionEn: "IFRS 16 took effect on 1 January 2019 and affects every IFRS company with lease agreements. Tiego's experiences from mapping and implementation.",
    image: "news/ifrs-books.jpg",
    imageStyle: "cover",
    imageAlt: "Närbild på bokryggarna till IFRS-standarderna",
    imageAltEn: "Close-up of the spines of the IFRS standards volumes",
    content: `**Inspelning av seminarium 11 april**\n\nMed den nya IFRS-standarden för leasingavtal, IFRS 16, som trädde i kraft den 1 januari 2019, berörs samtliga IFRS bolag som har hyresavtal i sin verksamhet. Detta medför att den tidigare klassificeringen operationell och finansiell leasing nu har ersatts med en modell där samtliga kontrakt redovisas i balansräkningen som en tillgång och som en skuld.\n\nTiego tillhandahåller både system och utbildning i ämnet och kan därmed lösa och förklara det för dig och ditt företag.`,
    contentEn: `**Recording of seminar on April 11**\n\nWith the new IFRS standard for lease agreements, IFRS 16, which came into force on January 1, 2019, all IFRS companies that have lease agreements in their operations are affected. This means that the previous classification of operating and finance leases has been replaced with a model where all contracts are reported in the balance sheet as an asset and as a liability.\n\nTiego provides both systems and training on the subject and can therefore solve and explain it for you and your company.`,
  },
];

export const newsArticles = [...unsortedArticles].sort(
  (a, b) => parseSwedishDate(b.date).getTime() - parseSwedishDate(a.date).getTime()
);
